/**
 * components/riskTerminal/RiskTerminal.jsx
 *
 * The Risk Terminal trade screen — a port of the trader's Claude Design
 * source, version 4, desktop screen 4a (handover/design/extracted/markup.html
 * and logic.js). Layout, spacing, colours, type, copy and behaviours are the
 * design's; the values come from GET /upstox/panel.
 *
 * Actions (upstox-037): rename, close, move / group / holding, intent,
 * the interrupt answer and dismiss call the existing /upstox endpoints
 * (api.js), refetch the panel on success, and show the server's refusal in
 * place on failure — never an optimistic change. Exit, modify and cancel
 * stay disabled: this terminal places no orders.
 *
 * Omitted in version 1 (upstox-036): the watchlist column, order entry and
 * its confirm step, the entry/orders divider, mobile (4b).
 *
 * Props: panel (the /upstox/panel body or null), fetchedAt (ms), now (ms,
 * ticking), error (last poll's failure or null), size ({width,height} to fix
 * the frame, as the harness does; otherwise it fills its container), api
 * (the write calls; the harness passes a fake), refetch (reload the panel
 * now), seed (harness only: an initial UI state to photograph).
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  C, FONT, btnGhost, btnRepair, iconBtnSm, icSm, popStyle, disabledLook, NO_ORDERS,
  money, badge, plainNotGraded, notComputedStyle, inr0, hhmm, mmss, signed, loadIcons, textareaStyle,
} from './theme';
import {
  gradeKind, notGradedTag, gradeTitle, trendOf, feedFaults, moneyOf, conditionView,
  calmNote, dashReason, sideLine, fillLine, trendStrip, freshnessOf, qtyText, priceText, avgText, orderCharges, rowCharges, chargesToday, sumKnown, DATA_STALE_MS, UPSTOX_SIGNIN_URL, mcxNote, orderTerms, closeness,
} from './model';
import { accountName, ordersNote } from './accounts';
import { HoverLayer } from './hoverCard';
import { OrdersNoteCtx } from './ordersNote';
import { AccountSegments, TrendBlock, FreshnessEnd } from './StatusStrip';
import { ColsCtx, useCols, useTrackColumns, cell, cbox, closedBuyW, closedNoteW } from './columns';
import MarginFooter from './MarginFooter';
import { flagKeys, groupLineParts, sharedGradeState, useGroupStep } from './groupLine';
import { realApi } from './api';
import { openUpstoxSignin, onUpstoxSignedIn } from './signin';   // upstox-053
import { Picker, ClosePanel, IntentControl, Refusal, Note, nameInputStyle } from './controls';
import Watchlist from './Watchlist';
import OrderTestBox from './OrderTestBox';
import { heldIndex } from './heldChips';          // upstox-084: held quantities on the desktop watchlist (the phone's function)          // upstox-082: the fenced live order test (hidden unless the server serves it)

const STOP_TITLE = 'stop not tracked — placed outside the terminal';
const NO_LEG_YET = 'The terminal has not registered this position yet — it can be grouped after the next poll';
const PREF_KEY = 'riskTerminal.view.v1';

function readPrefs() {
  try { return JSON.parse(window.localStorage.getItem(PREF_KEY)) || {}; } catch (e) { return {}; }
}
function writePrefs(p) {
  try { window.localStorage.setItem(PREF_KEY, JSON.stringify(p)); } catch (e) { /* view prefs only */ }
}
// upstox-050: Orders and Watchlist collapse independently to a labelled strip;
// the choice is remembered in the browser (blocked storage = both open)
const PANELS_KEY = 'riskTerminal.panels.v1';
function readPanels() {
  try {
    const v = JSON.parse(window.localStorage.getItem(PANELS_KEY));
    return { orders: !(v && v.orders === false), watch: !(v && v.watch === false) };
  } catch (e) { return { orders: true, watch: true }; }
}
function writePanels(p) {
  try { window.localStorage.setItem(PANELS_KEY, JSON.stringify(p)); } catch (e) { /* view pref only */ }
}
// upstox-085: the page grid (the Claude Design handoff). The positions panel IS the 1fr track — no cap, so no
// empty grey band on a wide monitor (the old 1,300 px cap on the panel inside a track that kept growing was
// the band). Orders flex 360–520 px with the viewport; the watchlist is fixed at 380. A collapsed panel is a 36 px strip.
export const ORDERS_TRACK = 'clamp(360px,23vw,520px)';
export const WATCH_TRACK = '380px';
export const STRIP_TRACK = '36px';
export const pageColumns = (ordersOpen, watchOpen) => ['minmax(0,1fr)', ordersOpen ? ORDERS_TRACK : STRIP_TRACK, watchOpen ? WATCH_TRACK : STRIP_TRACK].join(' ');
// the positions track's floor: below it the track scrolls sideways instead of squeezing the numbers
const POS_MIN = '560px';          // upstox-074: the columns now yield (columns.js); this is the floor, never a sideways scroll

/** A collapsed panel: a thin strip, its name sideways, a small count; click to expand. */
function PanelStrip({ name, counts = [], onOpen, title }) {
  return (
    <button type="button" data-strip={name} onClick={onOpen} data-tip={title || 'Expand ' + name.toLowerCase()}
      style={{ flex: '0 0 36px', width: '36px', minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', padding: '14px 0', background: C.paper2, border: 'none', borderRight: '1px solid ' + C.line, cursor: 'pointer', fontFamily: FONT }}>
      <span style={{ ...icSm, color: C.ink4 }}>keyboard_double_arrow_left</span>
      <span style={{ writingMode: 'vertical-rl', fontSize: '11px', letterSpacing: '0.14em', color: C.ink, fontWeight: 600, whiteSpace: 'nowrap' }}>{name}</span>
      {counts.filter(Boolean).map(c => (
        <span key={c.text} style={{ writingMode: 'vertical-rl', fontSize: '10px', letterSpacing: '0.06em', whiteSpace: 'nowrap', fontWeight: c.amber ? 700 : 400, color: c.amber ? C.amber : C.ink4 }}>{c.text}</span>
      ))}
    </button>
  );
}

// upstox-045: the rail's collapsed / expanded choice lasts the browser session
const RAIL_KEY = 'riskTerminal.rail.v1';
function readRailOpen() {
  try { const v = window.sessionStorage.getItem(RAIL_KEY); return v === null ? true : v === 'open'; } catch (e) { return true; }
}
function writeRailOpen(open) {
  try { window.sessionStorage.setItem(RAIL_KEY, open ? 'open' : 'collapsed'); } catch (e) { /* view pref only */ }
}

/**
 * upstox-045: the collapsed rail — one chip per condition in the server's
 * rank order, coloured by severity, on the header's own line. Chips that
 * don't fit are counted in "+N more"; no alert is ever hidden behind a count
 * alone. Clicking a chip expands the rail with that condition leading.
 */
function RailChips({ views, onPick }) {
  const box = useRef(null);
  const [fit, setFit] = useState(views.length);
  const [moreAt, setMoreAt] = useState(0);
  const key = views.map(v => v.id + ':' + v.chip).join('|');
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => {
      const W = el.clientWidth;
      const chips = Array.from(el.querySelectorAll('[data-chip]'));
      const right = c => c.offsetLeft + c.offsetWidth;
      if (!chips.length || right(chips[chips.length - 1]) <= W) { setFit(chips.length); return; }
      let f = 0;
      while (f < chips.length && right(chips[f]) <= W - 84) f += 1;
      setFit(f);
      setMoreAt(f ? right(chips[f - 1]) + 6 : 0);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => ro && ro.disconnect();
  }, [key]);
  const hidden = views.length - fit;
  return (
    <div ref={box} data-rail-chips style={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', height: '22px' }}>
      {views.map((v, i) => (
        <button key={v.id} type="button" data-chip={v.kindCode} onClick={() => onPick(v.id)} data-tip={v.kind + (v.text ? ' · ' + v.text : '') + ' — open'}
          style={{ flex: '0 0 auto', height: '22px', padding: '0 8px', fontFamily: FONT, fontSize: '9px', letterSpacing: '0.1em', fontWeight: 700, cursor: 'pointer',
            color: v.red ? '#fff' : C.amber, background: v.red ? C.red : C.amberTint, border: '1px solid ' + (v.red ? C.red : C.amberLine),
            visibility: i < fit ? 'visible' : 'hidden' }}>{v.chip}</button>
      ))}
      {hidden > 0 && (
        <div data-chip-more style={{ position: 'absolute', left: moreAt + 'px', top: 0, height: '22px', display: 'flex', alignItems: 'center', padding: '0 6px', fontSize: '10px', letterSpacing: '0.08em', color: C.ink2, fontWeight: 600, background: C.paper2 }}
          data-tip={views.slice(fit).map(v => v.chip).join('\n')}>+{hidden} more</div>
      )}
    </div>
  );
}

const SCOPED_CSS = `
.rt-root, .rt-root * { box-sizing:border-box; }
.rt-root ::-webkit-scrollbar { width:7px; height:7px; }
.rt-root ::-webkit-scrollbar-thumb { background:#ccc5b8; }
@keyframes rt-attn { 0%{opacity:0.3} 50%{opacity:1} 100%{opacity:0.3} }
.rt-root .rt-ib:not(:disabled):hover { background:#efece6 !important; border-color:#dbd6cb !important; }
.rt-root .rt-rename:not(:disabled):hover { background:#efece6 !important; }
.rt-root .rt-hd:hover { color:#1a1917 !important; }
.rt-root [data-positions][data-wide="true"] [data-group-row]:hover, .rt-root [data-positions][data-wide="true"] [data-leg-row]:hover { background:#f6f4ef; }   /* upstox-085: the hover tint on wide tracks */
.rt-root .rt-repair:not(:disabled):hover { background:#f8f6f2 !important; }
.rt-root .rt-seg:hover { background: var(--seg-tint) !important; }
.rt-root .rt-tip { text-decoration: underline dotted #878176; text-underline-offset: 3px; cursor: default; }
`;

function Icon({ name }) { return <span style={icSm}>{name}</span>; }

// upstox-067: the note on every order control — the account's `can_trade` decides what it says

function InertIconBtn({ icon, title, style, exit }) {
  const note = React.useContext(OrdersNoteCtx);
  // upstox-074 (item 13): an exit icon is inert but readable — #878176, default cursor, "<what> · not available yet"
  if (exit) {
    return (
      <button type="button" className="rt-ib" disabled aria-label={title} data-tip={title + ' · not available yet'} style={{ ...iconBtnSm, ...style, cursor: 'default', color: '#878176' }}>
        <Icon name={icon} />
      </button>
    );
  }
  return (
    <button type="button" className="rt-ib" disabled aria-label={title} data-tip={title + ' · ' + note} style={{ ...iconBtnSm, ...style, ...disabledLook }}>
      <Icon name={icon} />
    </button>
  );
}

function NotComputed({ children, style }) {
  return <div style={{ ...notComputedStyle, ...style }}>{children}</div>;
}

function IconBtn({ icon, title, onClick, style }) {
  return (
    <button type="button" className="rt-ib" aria-label={title} data-tip={title} style={{ ...iconBtnSm, ...style }}
      onClick={e => { e.stopPropagation(); onClick(); }}>
      <Icon name={icon} />
    </button>
  );
}

export default function RiskTerminal({ panel, fetchedAt, now, error, size, rootRef, api = realApi, refetch = () => {}, seed,
  watch = null, watchError = null, refreshWatch = () => {}, stream = null,
  accounts = null, account = null, onSwitchAccount = null }) {
  useEffect(() => { loadIcons(); }, []);
  const prefs = useMemo(readPrefs, []);
  const [st, setSt] = useState(() => ({
    railOpen: readRailOpen(), railLead: null, ...(() => { const pn = readPanels(); return { ordersOpen: pn.orders, watchOpen: pn.watch }; })(), open: {}, hov: null, posView: prefs.posView || 'grouped',
    posSort: prefs.posSort || { key: null, dir: 1 }, closedOpen: false, doneOpen: false,
    interruptFor: null, hiddenEpisodes: {},
    // upstox-037 actions
    renaming: null, draft: '', moving: null, grouping: null, closing: null, intentOpen: null,
    awaitingOpen: false, awaiting: null, holdChosen: false, intWhy: '', busy: null,
    errs: {}, notes: {},
    ...((seed && seed.ui) || {}),
  }));
  const set = patch => setSt(s => ({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) }));
  useEffect(() => { writePrefs({ posView: st.posView, posSort: st.posSort }); }, [st.posView, st.posSort]);
  useEffect(() => { writeRailOpen(st.railOpen); }, [st.railOpen]);
  // upstox-053: a sign-in finished in another tab → refresh at once
  useEffect(() => onUpstoxSignedIn(() => refetch()), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { writePanels({ orders: st.ordersOpen, watch: st.watchOpen }); }, [st.ordersOpen, st.watchOpen]);
  const [posEl, setPosEl] = useState(null);                       // upstox-074: the positions track, observed for its columns
  const vis = useTrackColumns(posEl, st.posView === 'flat' ? 110 : 0);
  // upstox-085: the group line's step (1 → 3), chosen by measuring; it starts again when the lines' content changes
  const lineSig = JSON.stringify([(panel && panel.status && panel.status.session_state) || '', ((panel && panel.structures && panel.structures.items) || []).map(x => [x.id, x.name, x.grade && [x.grade.computed, x.grade.value, x.grade.reason], !!x.regroup_mark_today, !!x.reading_switched_today, !!x.offside, !!x.has_problem]), st.open]);
  const gStep = useGroupStep(posEl, lineSig);
  const hv = k => ({ on: st.hov === k, enter: () => set({ hov: k }), leave: () => set(s => (s.hov === k ? { hov: null } : {})) });

  const frame = {
    position: 'relative', background: '#ffffff', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    fontFamily: FONT, color: C.ink,
    ...(size ? { width: size.width + 'px', height: size.height + 'px' } : { width: '100%', height: 'calc(100vh - 118px)', minHeight: '680px' }),
  };
  const dataAgeMs = fetchedAt ? Math.max(0, now - fetchedAt) : null;
  const stale = !!error || (dataAgeMs != null && dataAgeMs > DATA_STALE_MS);
  frame.border = '1px solid ' + (stale && panel ? C.amber : C.line);

  if (!panel) {
    return (
      <div ref={rootRef} className="rt-root" style={frame}>
        <style>{SCOPED_CSS}</style>
        <div style={{ padding: '18px' }}>
          {accounts && accounts.length > 1 && <div style={{ marginBottom: '10px' }}><AccountSegments accounts={accounts} account={account} status={null} onSwitch={onSwitchAccount} /></div>}
          <NotComputed>{error ? 'RISK TERMINAL UNAVAILABLE · ' + error.toUpperCase() : 'LOADING THE RISK TERMINAL…'}</NotComputed>
        </div>
      </div>
    );
  }

  const P = panel;
  const status = P.status || {};
  const sinceFetch = s => (s || 0) + (dataAgeMs || 0) / 1000;           // server seconds, advanced locally
  const elapsed = off => sinceFetch(off.elapsed_seconds);

  // ── lookups ────────────────────────────────────────────────────────────
  const todayItems = (P.structures && P.structures.items) || [];
  const unclassified = (P.unclassified && P.unclassified.items) || [];
  const holdingItems = (P.holdings && P.holdings.items) || [];
  const structuresById = Object.fromEntries(todayItems.map(s => [s.id, s]));
  const symbols = {};
  [...todayItems, ...holdingItems].forEach(s => (s.legs || []).forEach(l => { symbols[l.instrument_key] = l.trading_symbol; }));
  unclassified.forEach(l => { symbols[l.instrument_key] = l.trading_symbol; });
  ((P.closed_today && P.closed_today.items) || []).forEach(c => { if (c.trading_symbol) symbols[c.instrument_key] = c.trading_symbol; });
  ((P.orders && P.orders.items) || []).forEach(o => { if (o.trading_symbol) symbols[o.instrument_key] = o.trading_symbol; });
  // upstox-038: names, not keys — the panel's own map (the instrument master) first
  Object.assign(symbols, P.symbols || {});
  const symbolOf = k => symbols[k] || k || '—';
  const positionsReason = P.structures && !P.structures.positions_available ? P.structures.positions_reason : null;
  const priceMode = (((P.status || {}).feed || {}).prices || {}).mode;           // upstox-054

  // ── actions (upstox-037) ───────────────────────────────────────────────
  // One place for "the server said yes": refetch at once; notes and refusals
  // are keyed to where the action was taken.
  const note = (key, text) => set(s => ({ notes: { ...s.notes, [key]: text } }));
  const errOf = key => st.errs[key];
  const setErr = (key, text) => set(s => ({ errs: { ...s.errs, [key]: text } }));
  const clearKey = key => set(s => ({ errs: { ...s.errs, [key]: null }, notes: { ...s.notes, [key]: null } }));
  const once = async (key, fn, onOk) => {
    if (st.busy) return null;
    set(s => ({ busy: key, errs: { ...s.errs, [key]: null } }));
    const res = await fn();
    set({ busy: null });
    if (res.ok) { onOk && onOk(res); refetch(); } else setErr(key, res.message);
    return res;
  };
  const busy = key => st.busy === key;
  const minWhy = (((P.settings && P.settings.in_force) || {}).rationale_min_chars) || 40;
  const holdingSentence = res => (res && res.data && res.data.holding ? ' ' + res.data.holding.message : '');

  const startRename = s => set({ renaming: s.id, draft: s.name, closing: null });
  const saveRename = async s => {
    if (st.renaming !== s.id) return;
    const v = (st.draft || '').trim();
    if (!v || v === s.name) { set({ renaming: null }); return; }
    const res = await once('st' + s.id, () => api.rename(s.id, v), () => set({ renaming: null }));
    if (res && !res.ok) set({ renaming: s.id });             // keep the input; the refusal shows under it
  };
  const tryClose = s => {
    clearKey('st' + s.id);
    return once('st' + s.id, () => api.close(s.id, false), () => note('st' + s.id, null)).then(res => {
      if (res && !res.ok && res.detail && res.detail.error === 'structure_has_active_legs') {
        setErr('st' + s.id, null);
        set({ closing: { id: s.id, message: res.message, legs: res.detail.active_legs || [] } });
      }                                       // upstox-053: a plain close shows no message
    });
  };
  const forceClose = s => api.close(s.id, true);
  const closeDone = (s, res) => {
    const n = ((res.data && res.data.released_leg_ids) || []).length;
    set({ closing: null });
    note('rail', s.name + ' closed; ' + n + (n === 1 ? ' leg returned' : ' legs returned') + ' to unclassified.');
    refetch();
  };

  // the design's regroup picker for a grouped leg: another open group, a
  // holding, a new group (name typed), or back to unclassified
  const moveOptions = (s, l) => {
    const moved = (dest, res) => {
      const against = s.kind === 'confirmed' || s.kind === 'fading';
      set({ moving: null });
      // upstox-053: a plain move shows no message; only what the move does not show fades in
      const info = holdingSentence(res) + (against ? ' Both structures are marked REGROUPED for the rest of today. No timer was reset.' : '');
      if (info.trim()) note('moves', l.sym + ' moved into ' + dest + '.' + info);
      refetch();
    };
    return {
      options: [
        ...todayItems.filter(x => x.id !== s.id).map(x => ({ key: 't' + x.id, label: x.name, run: () => api.move(l.key, x.id), done: res => moved(x.name, res) })),
        ...holdingItems.map(x => ({ key: 'h' + x.id, label: x.name + ' · HOLDING', run: () => api.move(l.key, x.id), done: res => moved(x.name, res) })),
        { key: 'u', label: 'UNCLASSIFIED', run: () => api.unassign(l.key), done: res => moved('unclassified', res) },
      ],
      create: async name => {
        const c = await api.createStructure(name, 'TODAY');
        if (!c.ok) return c;
        const m = await api.move(l.key, c.data.structure.id);
        if (!m.ok) { refetch(); return { ...m, message: m.message + ' (The new group ' + name + ' was created and is empty.)' }; }
        return { ...m, createdName: name };
      },
      createdDone: res => moved(res.createdName, res),
    };
  };

  // GROUP / HOLDING for an unclassified leg
  const groupOptions = (o, mode) => {
    const done = (dest, res) => {
      set({ grouping: null });
      const info = holdingSentence(res);              // upstox-053: only the holding effect is news
      if (info.trim()) note('orphans', o.sym + (mode === 'holding' ? ' is now a holding in ' : ' grouped into ') + dest + '.' + info);
      refetch();
    };
    if (mode === 'holding') {
      return {
        options: holdingItems.map(x => ({ key: 'h' + x.id, label: x.name, run: () => api.holding(o.legId, { structure_id: x.id }), done: res => done(x.name, res) })),
        create: name => api.holding(o.legId, { new_structure_name: name }).then(r => ({ ...r, createdName: name })),
        createdDone: res => done(res.createdName, res),
      };
    }
    return {
      options: todayItems.map(x => ({ key: 't' + x.id, label: x.name, run: () => api.assign(o.legId, { structure_id: x.id }), done: res => done(x.name, res) })),
      create: name => api.assign(o.legId, { new_structure: { name, book: 'TODAY' } }).then(r => ({ ...r, createdName: name })),
      createdDone: res => done(res.createdName, res),
    };
  };
  const pickerFrom = (o, head, note_, amber, onCancel, newLabel, init) => (
    <Picker head={head} note={note_} amber={amber} newLabel={newLabel} initial={init}
      options={o.options.map(x => ({ key: x.key, label: x.label, run: async () => { const r = await x.run(); if (r.ok) x.done(r); return r; } }))}
      create={async name => { const r = await o.create(name); if (r.ok) o.createdDone(r); return r; }}
      onCancel={onCancel} />
  );

  const declare = (orderId, intent) => api.declareIntent(orderId, intent);
  const intentDone = () => { refetch(); if (st.awaitingOpen) loadAwaiting(); };
  const loadAwaiting = async () => {
    const r = await api.awaitingIntent();
    set({ awaiting: r.ok ? { items: (r.data && r.data.orders) || [], earlierCount: r.data && r.data.earlier_count } : { error: r.message } });
  };
  // upstox-038: orders before the tagging start are never counted, but load on demand
  const loadEarlier = async () => {
    const r = await api.awaitingIntent(true);
    set(s => ({ awaiting: r.ok ? { ...s.awaiting, earlier: (r.data && r.data.orders) || [] } : { ...s.awaiting, earlierError: r.message } }));
  };
  const toggleAwaiting = () => {
    if (!st.awaitingOpen) loadAwaiting();
    set(s => ({ awaitingOpen: !s.awaitingOpen }));
  };

  const dismiss = k => once('c' + k.id, () => api.dismiss(k.id));

  // upstox-044: switching the trend reading — confirmed first when any TODAY
  // group is against the trend (the server enforces the same and names them)
  const AGAINST = ['AGAINST_FADING', 'AGAINST_CONFIRMED'];
  const askSwitch = to => {
    const groups = todayItems.filter(x => x.grade && AGAINST.includes(x.grade.value))
      .map(x => ({ id: x.id, name: x.name, grade: x.grade.value }));
    if (!groups.length) { doSwitch(to, false); return; }
    const n = groups.length;
    set({ readingConfirm: { to, groups, message: n + (n === 1 ? ' group is' : ' groups are') + ' against the trend. Switching is recorded for review.' } });
  };
  const doSwitch = (to, confirm) => once('reading', () => api.switchReading(to, confirm), () => set({ readingConfirm: null }))
    .then(res => {
      if (res && !res.ok && res.detail && res.detail.error === 'confirm_required') {
        setErr('reading', null);
        set({ readingConfirm: { to, groups: res.detail.groups || [], message: res.detail.message } });
      }
    });

  // ── status bar ─────────────────────────────────────────────────────────
  const trend = trendOf(status);
  const faults = feedFaults(status, dataAgeMs, error, stream).map(f => ({
    ...f,
    wrapStyle: { display: 'flex', alignItems: 'baseline', gap: '7px', padding: '4px 9px', background: f.state === 'lost' ? C.redTint : C.amberTint, border: '1px solid ' + (f.state === 'lost' ? C.red : C.amber) },
    nameStyle: { fontSize: '10px', color: f.state === 'lost' ? C.red : C.amber, letterSpacing: '0.1em', fontWeight: 700 },
    valStyle: { fontSize: '11px', color: C.ink, fontVariantNumeric: 'tabular-nums', fontWeight: 500 },
  }));
  const tStrip = trendStrip(status);
  const fresh = freshnessOf(status, faults);
  const acct = status.account || {};
  const inForce = (P.settings && P.settings.in_force) || null;

  // ── money ──────────────────────────────────────────────────────────────
  const M = moneyOf(P.money, P.settings);
  const mT = money(M.total, 38);
  const CT = chargesToday(P.charges);                                   // upstox-046

  // ── conditions (the server's ranking, kept) ─────────────────────────────
  const conds = (P.conditions && P.conditions.active) || [];
  const cviews = conds.map(k => conditionView(k, { structuresById, elapsed, symbolOf, eodTime: inForce && inForce.end_of_day_check_time }));
  // upstox-045: a chip clicked in the collapsed rail leads; the rest keep rank order
  const lead = cviews.find(c => c.id === st.railLead) || cviews[0] || null;
  const queue = cviews.filter(c => c !== lead);
  const openInterrupt = id => set(s => ({ interruptFor: id, hiddenEpisodes: { ...s.hiddenEpisodes } }));
  const actOn = k => {
    if (k.actDisabled) return;
    if (k.kindCode === 'INTERRUPT_DUE') openInterrupt(k.structureId);
    else if (k.kindCode === 'ORDER_REJECTED') dismiss(k);
  };

  // the interrupt overlay: on its own for an active INTERRUPT_DUE, until put aside
  const interrupts = cviews.filter(c => c.kindCode === 'INTERRUPT_DUE');
  const shownInterrupt = interrupts.find(c => c.structureId === st.interruptFor)
    || interrupts.find(c => !st.hiddenEpisodes[c.episodeId]);

  // ── positions ──────────────────────────────────────────────────────────
  const legView = (l, extra) => {
    const lm = money(l.pnl, 12);
    return {
      key: l.leg_id ?? l.instrument_key, mcx: mcxNote(l.instrument_key), sym: l.trading_symbol || symbolOf(l.instrument_key), intent: l.opening_intent || '—',
      intentTitle: l.opening_intent ? "the opening order's declared intent" : 'no declared intent on the opening order',
      qty: qtyText(l.quantity), avg: avgText(l.average_price), ltp: priceText(l.last_price),
      // upstox-054: when only some open rows lack a Kite tick, each says so
      ltpUpstox: priceMode === 'mixed' && l.price_source === 'upstox',
      ltpTitle: l.price_source === 'kite' ? 'Kite live price' : l.price_source === 'upstox' ? "Upstox's price" + (l.price_note ? ' · ' + l.price_note : '') : undefined,
      pl: lm.text, rawPl: l.pnl, rawQty: l.quantity, rawAvg: l.average_price, rawLtp: l.last_price,
      plStyle: { ...cbox(vis, 'pl'), textAlign: 'right', fontSize: '12px', fontVariantNumeric: 'tabular-nums', color: l.pnl == null ? C.ink5 : l.pnl < 0 ? '#000' : C.ink3, fontWeight: l.pnl < 0 ? 500 : 400 },
      unknownTitle: l.pnl == null ? (l.pnl_reason || positionsReason || 'no broker row for it') : '',
      stopFailed: !!l.stop_failed,                  // upstox-074: no stop is armed yet anywhere, so this is false today; the NO STOP box is built for when one fails
      // upstox-039: reopened the same day, same direction — rejoined its group at first sighting
      rejoined: l.rejoined_at ? 'rejoined ' + hhmm(l.rejoined_at) : '',
      ch: rowCharges(l.charges, [{ sym: l.trading_symbol || symbolOf(l.instrument_key), c: l.charges }]),
      ...extra,
    };
  };

  const structures = todayItems.map(s => {
    const kind = gradeKind(s.grade);
    const ng = kind === 'ungraded' ? notGradedTag(s.grade, status, now) : null;          // upstox-074: how "not graded" reads
    const b = ng ? (ng.plain ? { label: ng.label, style: plainNotGraded } : badge('ungraded', ng.label)) : badge(kind);
    const h = hv('st' + s.id);
    const flagged = kind === 'confirmed';
    const legs = s.legs || [];
    const off = s.offside;
    const el = off ? elapsed(off) : 0;
    const amberS = off && off.thresholds ? off.thresholds.amber_seconds : null;
    const redS = off && off.thresholds ? off.thresholds.red_seconds : null;
    const due = !!off && redS != null && el >= redS;
    const gc = due ? C.red : amberS != null && el >= amberS ? C.amber : C.ink3;
    const frac = redS ? Math.min(el / redS, 1) : 0;
    const pl = legs.length ? sumKnown(legs.map(l => l.pnl)) : null;
    const m = money(pl, 16);
    const isOpen = st.open[s.id] ?? !!s.has_problem;
    const nd = s.net_delta || {};
    const delta = nd.computed ? (nd.net_delta < 0 ? '−' : '+') + Math.abs(Math.round(nd.net_delta)) : '— (' + (nd.reason || 'not computed') + ')';
    const firstSeen = legs.map(l => l.first_seen_at).filter(Boolean).sort()[0];
    const nLegs = legs.length + (legs.length === 1 ? ' leg' : ' legs');
    return {
      id: s.id, name: s.name, kind, badge: b.label, badgeStyle: b.style, badgeTitle: ng && ng.tip ? ng.tip : gradeTitle(s.grade),
      enter: h.enter, leave: h.leave, hovered: h.on,
      metaStyle: { fontSize: '11px', color: C.ink4, whiteSpace: 'nowrap', display: h.on ? 'block' : 'none', flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
      meta: 'Δ ' + delta + ' · ' + nLegs + (firstSeen ? ' · entered ' + hhmm(firstSeen) : ''),
      collapsedNote: !isOpen && !h.on ? nLegs : false,
      timer: off ? mmss(el) : '', due,
      timerStyle: off ? { fontSize: '11px', color: gc, fontVariantNumeric: 'tabular-nums', fontWeight: 700, letterSpacing: '0.04em' } : { display: 'none' },
      hasGauge: !!off && redS != null,
      gaugeTitle: amberS != null ? 'Warning at ' + mmss(amberS) + ' · interrupt at ' + mmss(redS) : '',
      gaugeStyle: { width: '56px', height: '6px', flex: '0 0 56px', background: due ? C.red : 'linear-gradient(90deg,' + (amberS != null && el >= amberS ? C.amber : C.line4) + ' ' + Math.round(frac * 100) + '%, #f1eee8 0)', border: '1px solid ' + (due ? C.red : C.line) },
      gradeLabel: kind === 'ungraded' ? b.label : '', flags: flagKeys(s),
      pl: m.text, plTip: dashReason(legs, positionsReason), plStyle: { ...m.style, ...cbox(vis, 'pl'), textAlign: 'right' },
      ch: rowCharges(s.charges, (s.legs || []).map(l => ({ sym: l.trading_symbol || symbolOf(l.instrument_key), c: l.charges }))),
      wrapStyle: { background: flagged ? C.redTint : kind === 'fading' ? C.fadingTint : '#fff', marginBottom: '4px' },   // upstox-055: spacing, not lines
      caret: isOpen ? '▾' : '▸', open: isOpen,
      toggle: () => set(x => ({ open: { ...x.open, [s.id]: !isOpen } })),
      renameBtnStyle: { width: '24px', height: '24px', flex: '0 0 24px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', color: C.ink3, border: 'none', cursor: 'pointer', visibility: h.on ? 'visible' : 'hidden' },
      legs: legs.map(l => legView(l, { grade: kind, grName: s.name, grBadge: ng ? (ng.plain ? { label: ng.label, style: plainNotGraded } : badge('ungraded', ng.label)) : null, grTimer: off ? mmss(el) : null })),
    };
  });

  // upstox-085: a grey grade state every group shares sits on the POSITIONS heading from step 2
  const sharedGrade = sharedGradeState(structures);
  const FOLD_BOX = { fontSize: '9px', letterSpacing: '0.1em', fontWeight: 700, padding: '1px 5px', whiteSpace: 'nowrap', flex: '0 0 auto', cursor: 'default' };
  const FLAG_BOX = { fontSize: '9px', letterSpacing: '0.1em', fontWeight: 700, padding: '2px 6px', color: C.amber, border: '1px dashed ' + C.amber, whiteSpace: 'nowrap', flex: '0 0 auto' };

  // by instrument: every TODAY leg, sortable
  const sort = st.posSort;
  let rows = [];
  structures.forEach((s, si) => s.legs.forEach((l, li) => rows.push({ l, ord: si * 1000 + li })));
  if (sort.key) {
    const val = r => ({ sym: r.l.sym, group: r.l.grName, intent: r.l.intent, qty: r.l.rawQty, avg: r.l.rawAvg, ltp: r.l.rawLtp, pl: r.l.rawPl }[sort.key]);
    rows.sort((a, b) => {
      const x = val(a), y = val(b);
      if (x == null && y == null) return a.ord - b.ord;
      if (x == null) return 1;                      // unknowns last, whichever direction
      if (y == null) return -1;
      const c = typeof x === 'string' ? x.localeCompare(y) : x - y;
      return (c || a.ord - b.ord) * sort.dir;
    });
  }
  const tint = g => (g === 'confirmed' ? C.redTint : g === 'fading' ? C.fadingTint : '#fff');
  const flatLegs = rows.map(({ l }) => {
    const tags = [];
    if (l.grade !== 'aligned') { const bb = l.grBadge || badge(l.grade); tags.push({ label: bb.label, style: { ...bb.style, fontSize: '9px', padding: l.grade === 'fading' ? '0' : '2px 6px' } }); }
    if (l.grTimer) tags.push({ label: l.grTimer, style: { fontSize: '11px', color: C.red, fontWeight: 700, fontVariantNumeric: 'tabular-nums' } });
    return { ...l, tags, plStyle: { ...l.plStyle, fontSize: '13px' },
      rowStyle: { display: 'flex', alignItems: 'center', padding: '6px 14px', background: tint(l.grade), marginBottom: '1px' } };
  });
  const head = (key, label, w, align) => {
    const on = sort.key === key;
    return {
      key, label: label + (on ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''),
      pick: () => set(x => ({ posSort: x.posSort.key === key ? (x.posSort.dir === 1 ? { key, dir: -1 } : { key: null, dir: 1 }) : { key, dir: 1 } })),
      style: { ...(w ? { width: w + 'px', flex: '0 0 ' + w + 'px' } : { flex: 1 }), display: 'flex', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: FONT, fontSize: '9px', letterSpacing: '0.1em', color: on ? C.ink : C.ink4, fontWeight: on ? 700 : 400 },
    };
  };
  const flatHeads = [head('sym', 'INSTRUMENT'), head('group', 'GROUP', 110), head('intent', 'INTENT', 60), head('qty', 'QTY', 56, 'right'), head('avg', 'AVG', 70, 'right'), head('ltp', 'LTP', 70, 'right'), head('pl', 'P&L', 110, 'right')];
  const segBtn = (k, lbl) => ({ k, label: lbl, style: { height: '22px', padding: '0 9px', background: st.posView === k ? C.ink : '#fff', color: st.posView === k ? '#fff' : C.ink3, border: '1px solid ' + (st.posView === k ? C.ink : C.line3), fontFamily: FONT, fontSize: '9px', letterSpacing: '0.1em', cursor: 'pointer', fontWeight: st.posView === k ? 600 : 400 } });
  const posSeg = [segBtn('grouped', 'GROUPED'), segBtn('flat', 'BY INSTRUMENT')];

  // unclassified ("orphans" in the design)
  const orphans = unclassified.map(l => {
    const om = money(l.pnl, 16);
    const left = l.deadline_state === 'running' ? Math.max(0, (l.seconds_left || 0) - (dataAgeMs || 0) / 1000) : null;
    const short = l.deadline_state === 'running'
      ? (left >= 60 ? Math.ceil(left / 60) + ' min to reclassify' : Math.round(left) + 's to reclassify')
      : l.deadline_state === 'passed' ? 'reclassify window passed' : 'no same-day window';
    return {
      key: l.leg_id ?? l.instrument_key, mcx: mcxNote(l.instrument_key), legId: l.leg_id, sym: l.trading_symbol || symbolOf(l.instrument_key), short,
      meta: qtyText(l.quantity) + ' · first seen ' + hhmm(l.first_seen_at) + (l.reclassify_deadline ? ' · window ends ' + hhmm(l.reclassify_deadline) : ''),
      pl: om.text, plTip: l.pnl == null ? dashReason([l], positionsReason) : null, plStyle: { ...om.style, ...cbox(vis, 'pl'), textAlign: 'right' }, ch: rowCharges(l.charges, [{ sym: l.trading_symbol || symbolOf(l.instrument_key), c: l.charges }]),
    };
  });

  // holdings: HOLDING structures, no grade
  const holdings = holdingItems.map(hs => {
    const legs = hs.legs || [];
    const hm = money(legs.length ? sumKnown(legs.map(l => l.pnl)) : null, 16);
    return { key: hs.id, name: hs.name, meta: legs.length + (legs.length === 1 ? ' leg' : ' legs') + ' · outside today’s limit', pl: hm.text, plTip: dashReason(legs, positionsReason), plStyle: { ...hm.style, ...cbox(vis, 'pl'), textAlign: 'right' }, ch: rowCharges(hs.charges, legs.map(l => ({ sym: l.trading_symbol || symbolOf(l.instrument_key), c: l.charges }))), legs: legs.map(l => legView(l)) };
  });

  // closed today: legs exited today, each flagged if it broke a rule
  const closedItems = (P.closed_today && P.closed_today.items) || [];
  const closed = closedItems.map(c => {
    const b = c.broke_rule ? badge('confirmed') : badge('aligned');
    // upstox-038: one row per instrument Upstox lists as closed today, its whole figure
    const lm = money(c.pnl !== undefined ? c.pnl : c.realised, 12);         // upstox-053: leg weight
    return { key: c.instrument_key, name: noYear(c.trading_symbol || symbolOf(c.instrument_key)), group: c.structure_name || 'unclassified', time: hhmm(c.closed_at) || '—', noLeg: c.has_leg === false, badge: c.broke_rule ? 'BROKE A RULE' : '', badgeStyle: b.style, pl: lm.text, plStyle: { ...cbox(vis, 'pl'), textAlign: 'right', fontSize: '12px', fontVariantNumeric: 'tabular-nums', color: (c.pnl ?? c.realised) == null ? C.ink5 : (c.pnl ?? c.realised) < 0 ? '#000' : C.ink3, fontWeight: (c.pnl ?? c.realised) < 0 ? 500 : 400 }, ch: rowCharges(c.charges, [{ sym: c.trading_symbol || symbolOf(c.instrument_key), c: c.charges }]),
      // upstox-047: Upstox's own averages ("—" when unknown, never 0), LTP, the quantities on hover
      avgBuy: avgText(c.avg_buy), avgSell: avgText(c.avg_sell), ltp: priceText(c.last_price),
      qtyNote: 'bought ' + (c.buy_quantity == null ? '?' : Number(c.buy_quantity).toLocaleString('en-IN')) + ' · sold ' + (c.sell_quantity == null ? '?' : Number(c.sell_quantity).toLocaleString('en-IN')) };
  });
  const closedSum = sumKnown(closedItems.map(c => (c.pnl !== undefined ? c.pnl : c.realised)));
  const broke = closedItems.filter(c => c.broke_rule).length;
  // upstox-053: the group header's totals — the server's sum of the rows
  const ct = (P.closed_today && P.closed_today.totals) || null;
  const closedTotalPl = ct ? ct.pnl : closedSum;
  const closedTotalM = money(closedTotalPl, 16);
  const closedTotalCh = rowCharges(ct ? ct.charges : null, closedItems.map(c => ({ sym: c.trading_symbol || symbolOf(c.instrument_key), c: c.charges })));
  const closedLabel = closedItems.length
    ? closedItems.length + (closedItems.length === 1 ? ' position' : ' positions') + (broke ? ' · ' + broke + ' broke a rule' : '')
    : 'none';

  // ── orders ─────────────────────────────────────────────────────────────
  const orders = (P.orders && P.orders.items) || [];
  const isWorking = o => o.status_group === 'OPEN' || o.status_group === 'PARTIAL' || !o.status_group;
  const flagOf = o => (o.after_limit_over ? 'AFTER LIMIT' : o.after_limit_red ? 'AFTER RED' : '');
  const working = orders.filter(isWorking).map(o => {
    const h = hv('w' + o.upstox_order_id);
    return {
      key: o.upstox_order_id, sym: o.trading_symbol || o.instrument_key, flag: flagOf(o),
      short: o.side + ' ' + (o.status_group === 'PARTIAL' && (o.filled_quantity || 0) > 0 ? (o.filled_quantity || 0) + '/' + o.quantity : o.quantity) + (o.status_group ? '' : ' · ' + String(o.order_status || 'status unknown').toUpperCase()),
      detail: o.order_type + ' · ' + o.quantity + ' qty · intent ' + (o.intent || 'not declared') + ' · ' + String(o.ts || '').slice(11, 16),
      on: h.on, enter: h.enter, leave: h.leave, order: o, ch: orderCharges(o.charges),
      terms: orderTerms(o), cl: closeness(o),                         // upstox-069
    };
  });
  const done = orders.filter(o => !isWorking(o));
  const rejects = done.filter(o => o.status_group === 'REJECTED');
  const nFilled = done.filter(o => o.status_group === 'FILLED').length;
  const nCancelled = done.filter(o => o.status_group === 'CANCELLED').length;
  const awaiting = (P.orders && P.orders.awaiting_intent_count) || 0;

  // ── rail styles ─────────────────────────────────────────────────────────
  const n = cviews.length;
  const leadStyle = lead ? { flex: '0 0 440px', background: lead.red ? C.redTint : C.amberTint, border: '1px solid ' + (lead.red ? C.red : C.amberLine), padding: '12px 14px' } : {};
  const leadActStyle = lead ? { height: '32px', padding: '0 16px', background: lead.red ? C.red : C.ink, color: '#fff', border: 'none', fontFamily: FONT, fontSize: '11px', letterSpacing: '0.1em', cursor: 'pointer', fontWeight: 600, ...(lead.actDisabled ? disabledLook : {}) } : {};
  const leadAct2Style = { height: '32px', padding: '0 14px', background: '#fff', color: C.ink, border: '1px solid ' + C.line4, fontFamily: FONT, fontSize: '11px', letterSpacing: '0.08em', ...disabledLook };

  const limitHeadStyle = { whiteSpace: 'nowrap', fontSize: '11px', textTransform: 'uppercase', color: !M.computed ? C.ink5 : M.up ? C.ink3 : M.zone === 'UNDER' ? C.ink : M.hue, letterSpacing: '0.1em', fontWeight: 600 };

  // ── interrupt overlay values ────────────────────────────────────────────
  let ov = null;
  if (shownInterrupt) {
    const s = structuresById[shownInterrupt.structureId] || { legs: [] };
    const el = s.offside ? elapsed(s.offside) : 0;
    const legs = s.legs || [];
    const spl = legs.length ? sumKnown(legs.map(l => l.pnl)) : null;
    const cond = conds.find(k => k.id === shownInterrupt.id) || {};
    ov = {
      timer: mmss(el), episodeId: shownInterrupt.episodeId,
      head: (s.name || (cond.detail && cond.detail.structure_name) || 'This structure') + ' has been held against a confirmed trend for ' + Math.floor(el / 60) + ' minutes. Your rules say this position should not exist.',
      facts: [
        { k: 'STRUCTURE P&L', v: money(spl, 18).text, c: spl == null ? C.ink5 : spl < 0 ? '#000' : C.ink3, w: spl < 0 ? 600 : 400 },
        { k: 'INTERRUPT DUE SINCE', v: hhmm(cond.detail && cond.detail.raised_at) || '—', c: C.ink, w: 600 },
        { k: 'DAY LIMIT', v: M.computed ? M.short.toUpperCase() : '—', c: M.hue === C.line4 ? C.ink : M.hue, w: 600 },
        { k: 'TREND', v: trend.label.replace(' TREND', ''), c: C.ink, w: 400 },
      ],
      // recorded, never executed (the settled rule): EXIT and REDUCE say so plainly
      choices: [
        { label: 'EXIT', sub: 'Records your decision. Exit all ' + legs.length + (legs.length === 1 ? ' leg' : ' legs') + ' in the Upstox app — this terminal places no orders yet.', kind: 'exit', response: 'EXIT' },
        { label: 'REDUCE ½', sub: 'Records your decision. Halve it in the Upstox app — this terminal places no orders yet. Timer keeps running.', kind: 'reduce', response: 'REDUCE' },
        { label: 'HOLD', sub: 'Requires a written reason. Goes to weekly review.', kind: 'hold', response: 'HOLD' },
      ],
    };
  }
  const answered = epId => set(s => ({ interruptFor: null, holdChosen: false, intWhy: '', hiddenEpisodes: { ...s.hiddenEpisodes, [epId]: true } }));
  const answer = response => (response === 'HOLD'
    ? set({ holdChosen: true })
    : once('int', () => api.answer(ov.episodeId, response), () => answered(ov.episodeId)));
  const intWhyOk = st.intWhy.trim().length >= minWhy;
  const commitHold = () => intWhyOk && once('int', () => api.answer(ov.episodeId, 'HOLD', st.intWhy.trim()), () => answered(ov.episodeId));

  // a group's name: the design's inline rename (pencil → input; Enter or
  // leaving the field saves, Escape cancels), plus close
  const renderTitle = s => (st.renaming === s.id ? (
    <input autoFocus value={st.draft} style={nameInputStyle}
      onClick={e => e.stopPropagation()}
      onChange={e => set({ draft: e.target.value })}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.stopPropagation(); set({ renaming: null }); clearKey('st' + s.id); } }}
      onBlur={() => saveRename(s)} />
  ) : (
    <>
      <div data-group-name style={{ fontSize: '14px', color: C.ink, fontWeight: 600, letterSpacing: '0.01em', flex: '0 0 auto', whiteSpace: 'nowrap' }}>{s.name}</div>
      <button type="button" className="rt-rename" style={s.renameBtnStyle} data-tip="Rename this structure" onClick={e => { e.stopPropagation(); startRename(s); }}><Icon name="edit" /></button>
      <button type="button" className="rt-rename" style={{ ...s.renameBtnStyle, ...(busy('st' + s.id) ? { opacity: 0.5, cursor: 'wait' } : {}) }} data-tip="Close this group — refused while it has open legs"
        onClick={e => { e.stopPropagation(); tryClose(s); }}><Icon name="close" /></button>
    </>
  ));
  // under a group's row: its refusal, or the close-refused panel
  const renderGroupPanels = s => (
    <>
      <Refusal text={errOf('st' + s.id)} style={{ margin: '0 14px 8px 40px' }} />
      {st.closing && st.closing.id === s.id && (
        <ClosePanel name={s.name} message={st.closing.message} legs={st.closing.legs} symbolOf={symbolOf}
          initialError={seed && seed.closeError}
          forceClose={() => forceClose(s)} onCancel={() => set({ closing: null })} onDone={res => closeDone(s, res)} />
      )}
    </>
  );

  // ════════════════════════════════════════════════════════════════════════
  return (
    <OrdersNoteCtx.Provider value={ordersNote(account, NO_ORDERS)}>
    <div ref={rootRef} className="rt-root" style={frame} data-account={account ? accountName(account) : undefined}>
      <style>{SCOPED_CSS}</style>
      <HoverLayer />

      {/* STATUS (upstox-074: one segmented account switch · the trend with its radio list · freshness in one place) */}
      <div data-status-strip style={{ minHeight: '52px', flex: '0 0 auto', borderBottom: '1px solid ' + C.line, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: '4px', background: C.paper2 }}>
        <div style={{ padding: '6px 18px' }}>
          <AccountSegments accounts={accounts} account={account} status={status} onSwitch={onSwitchAccount} />
        </div>
        <div style={{ width: '1px', alignSelf: 'stretch', background: C.line }} />
        <TrendBlock trend={trend} strip={tStrip} note={status.trend_note} busy={busy('reading')} onPick={askSwitch}
          shared={acct.shared_with} lastSwitch={status.trend && status.trend.last_switch}>
          {st.readingConfirm && (
            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: '12px', zIndex: 40, width: '420px', padding: '10px 12px', background: '#fff', border: '1px dashed ' + C.line3, boxShadow: '0 4px 12px rgba(26,25,23,0.08)' }}>
              <div style={{ fontSize: '10px', color: C.ink4, letterSpacing: '0.08em', marginBottom: '6px' }}>{'SWITCH TO THE ' + st.readingConfirm.to + ' READING'}</div>
              <div style={{ fontSize: '12px', color: C.amber, fontWeight: 500, lineHeight: 1.45, marginBottom: '8px' }}>{st.readingConfirm.message}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: C.line2, border: '1px solid ' + C.line2, marginBottom: '10px' }}>
                {st.readingConfirm.groups.map(g => (
                  <div key={g.id} style={{ display: 'flex', padding: '5px 10px', background: '#fff', fontSize: '12px', color: C.ink, gap: '8px' }}>
                    <span style={{ flex: 1 }}>{g.name}</span>
                    <span style={{ fontSize: '9px', letterSpacing: '0.08em', color: g.grade === 'AGAINST_CONFIRMED' ? C.red : C.amber, fontWeight: 600 }}>{String(g.grade || '').replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '11px', color: C.ink3, lineHeight: 1.45, marginBottom: '8px' }}>A group still against under the new reading keeps its episode and timers. One no longer against ends its episode "reading switched" and is marked for the rest of today.</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button type="button" disabled={busy('reading')} onClick={() => doSwitch(st.readingConfirm.to, true)}
                  style={{ height: '28px', padding: '0 11px', background: C.ink, color: '#fff', border: '1px solid ' + C.ink, fontFamily: FONT, fontSize: '10px', letterSpacing: '0.06em', cursor: 'pointer', fontWeight: 600 }}>SWITCH</button>
                <button type="button" onClick={() => set({ readingConfirm: null })}
                  style={{ height: '28px', padding: '0 10px', background: 'transparent', color: C.ink3, border: '1px solid ' + C.line3, fontFamily: FONT, fontSize: '10px', letterSpacing: '0.06em', cursor: 'pointer' }}>CANCEL</button>
              </div>
              <Refusal text={errOf('reading')} />
            </div>
          )}
          {!st.readingConfirm && errOf('reading') && <div style={{ ...popStyle, background: C.red }}>{errOf('reading')}</div>}
        </TrendBlock>
        <div style={{ flex: 1 }} />
        <div style={{ padding: '0 18px', display: 'flex', alignItems: 'center', borderLeft: '1px solid ' + C.line, alignSelf: 'stretch' }}>
          <FreshnessEnd fresh={fresh} dataAgeMs={dataAgeMs} signinUrl={UPSTOX_SIGNIN_URL} onSignIn={openUpstoxSignin}
            dataTip={(stream && stream.mode === 'polling' ? 'Stream down — polled every 5 s' : 'Live stream') + ' · last update ' + (fetchedAt ? new Date(fetchedAt).toLocaleTimeString('en-IN', { hour12: false }) : '—')} />
        </div>
      </div>

      {/* ORDER TEST (upstox-082): renders nothing unless the server serves it to the trader on account #1 */}
      {account && !seed && <OrderTestBox account={account} />}

      {/* ATTENTION RAIL */}
      <div style={{ flex: '0 0 auto', background: n ? C.paper2 : '#fff', borderBottom: '1px solid ' + (n ? C.redLine : C.line) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: n && st.railOpen ? '9px 18px 0 18px' : '9px 18px' }}>
          <div style={n ? { width: '8px', height: '8px', background: lead.red ? C.red : C.amber, animation: 'rt-attn 700ms ease-out 3' } : { width: '8px', height: '8px', borderRadius: '50%', background: '#aba396' }} />
          <div style={{ fontSize: '11px', letterSpacing: '0.12em', color: n ? C.ink : C.ink3, fontWeight: 600, flex: '0 0 auto' }}>{n ? 'NEEDS AN ANSWER' : 'NOTHING NEEDS AN ANSWER'}</div>
          {n > 0 && !st.railOpen
            ? <RailChips views={cviews} onPick={id => set({ railOpen: true, railLead: id })} />
            : <div style={{ flex: 1 }} />}
          <div style={{ fontSize: '11px', color: C.ink4 }}>{n ? '' : calmNote(P.conditions)}</div>
          {n > 0 && (
            <button type="button" onClick={() => set(s => ({ railOpen: !s.railOpen, railLead: null }))} style={{ flex: '0 0 auto', height: '22px', padding: '0 8px', background: 'transparent', color: C.ink3, border: '1px solid ' + C.line3, fontFamily: FONT, fontSize: '9px', letterSpacing: '0.1em', cursor: 'pointer' }}>
              {st.railOpen ? 'COLLAPSE' : 'EXPAND'}
            </button>
          )}
        </div>
        {n > 0 && st.railOpen && (
          <div style={{ display: 'flex', alignItems: 'stretch', gap: '18px', padding: '10px 18px 14px 18px' }}>
            <div style={leadStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ fontSize: '10px', letterSpacing: '0.12em', color: lead.red ? C.red : C.amber, fontWeight: 700 }}>{lead.kind}</div>
                {lead.timer ? <div style={{ fontSize: '12px', color: C.red, fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{lead.timer}</div> : null}
              </div>
              <div data-lead-text data-tip={lead.keyTip || undefined} style={{ fontSize: '16px', color: C.ink, fontWeight: 500, marginTop: '6px', lineHeight: 1.3 }}>{lead.text}</div>
              {lead.legs && lead.legs.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '10px' }}>
                  {lead.legs.map((g, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', background: '#fff' }}>
                      <div style={{ fontSize: '12px', color: C.ink, fontWeight: 400, flex: 1 }}>{g[0]}</div>
                      <div style={{ fontSize: '11px', color: C.ink3 }}>{g[1]}</div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                <button type="button" style={{ ...leadActStyle, ...(busy('c' + lead.id) ? { opacity: 0.5, cursor: 'wait' } : {}) }} disabled={lead.actDisabled} data-tip={lead.actTitle}
                  onClick={() => actOn(lead)}>{lead.actLabel}</button>
                {lead.act2Label && <button type="button" style={leadAct2Style} disabled data-tip={lead.actTitle}>{lead.act2Label}</button>}
              </div>
              <Refusal text={errOf('c' + lead.id)} />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
              {queue.length > 0 && <div style={{ fontSize: '9px', color: C.ink4, letterSpacing: '0.12em', paddingBottom: '1px' }}>QUEUED BEHIND IT</div>}
              {queue.map(k => {
                const h = hv('q' + k.id);
                const kerr = errOf('c' + k.id);
                return (
                  <div key={k.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fff', padding: '5px 6px' }} onMouseEnter={h.enter} onMouseLeave={h.leave}>
                      <div style={{ fontSize: '9px', letterSpacing: '0.1em', color: k.red ? '#fff' : C.amber, background: k.red ? C.red : C.amberTint, padding: '3px 7px', fontWeight: 700, flex: '0 0 auto', minWidth: '118px', textAlign: 'center' }}>{k.kind}</div>
                      <div style={{ fontSize: '11px', color: C.ink2, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-tip={k.keyTip || undefined}>{k.text}</div>
                      <button type="button" disabled={k.actDisabled || busy('c' + k.id)} data-tip={k.actTitle}
                        onClick={() => actOn(k)}
                        style={{ ...btnGhost, height: '24px', fontSize: '9px', color: C.ink2, visibility: h.on || kerr ? 'visible' : 'hidden', ...(k.actDisabled ? disabledLook : {}) }}>{k.actLabel}</button>
                    </div>
                    <Refusal text={kerr} style={{ marginTop: '2px' }} />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div data-page-grid style={{ flex: 1, display: 'grid', gridTemplateColumns: pageColumns(st.ordersOpen, st.watchOpen), gridTemplateRows: 'minmax(0,1fr)', minHeight: 0 }}>

        {/* ══ TRACK 1 · POSITIONS ══ (upstox-085: the 1fr track itself — no cap; only the name column grows) */}
        <div style={{ minWidth: 0, display: 'flex', borderRight: '1px solid ' + C.line, background: '#fff', overflowX: 'auto', overflowY: 'hidden' }}>
        <ColsCtx.Provider value={vis}>
        <div data-positions data-wide={vis.intent ? 'true' : 'false'} ref={setPosEl} style={{ flex: '1 1 auto', width: '100%', minWidth: POS_MIN, display: 'flex', flexDirection: 'column', background: '#fff' }}>

          {/* upstox-086: the band is two rows — the Trading book (the day-limit bar inside it, HOLDINGS in its corner) and the margin footer */}
          <div data-summary-band data-past-limit={M.over ? 'true' : undefined} style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', borderBottom: '1px solid ' + (M.over ? '#eebcae' : C.line), background: M.over ? C.redTint : '#fff' }}>
            <div style={{ padding: '16px 20px 14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0, flex: 1, cursor: 'default' }}>
                  <div data-book-label style={{ fontSize: '10px', letterSpacing: '0.12em', color: M.over ? C.red : C.ink4, fontWeight: M.over ? 700 : 400 }} data-tip="Trading book: realised + unrealised from entry (the Upstox app's split). Holdings are outside it.">
                    {!M.computed ? 'TRADING BOOK' : M.over ? 'TRADING BOOK · PAST DAY LIMIT' : M.total < 0 ? 'TRADING BOOK · DOWN' : 'TRADING BOOK · UP'}
                  </div>
                  {M.computed ? (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '22px', flexWrap: 'wrap' }}>
                      <div style={{ ...(M.total < 0 ? mT.style : { fontSize: '38px', color: C.ink3, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', fontWeight: 400 }), lineHeight: 1 }}>{mT.text}</div>
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {[['REALISED', M.realised], ['UNREALISED', M.unrealised]].map(([lbl, v]) => (
                          <div key={lbl} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ fontSize: '9.5px', color: C.ink4, letterSpacing: '0.12em' }}>{lbl}</div>
                            <div data-band-figure style={{ fontSize: '16px', fontVariantNumeric: 'tabular-nums', color: v == null ? C.ink5 : v < 0 ? '#000' : C.ink3, fontWeight: v < 0 ? 600 : 400 }}>{v == null ? '—' : v === 0 ? '₹0' : signed(v).replace(/^([−+])/, '$1₹')}</div>
                          </div>
                        ))}
                        {/* upstox-046: shown beside, never deducted */}
                        <HoverCard text={CT.title} open={st.chargesOpen === 'today'} align="left">
                          <div data-charges-today style={{ display: 'flex', flexDirection: 'column', gap: '2px', cursor: 'default' }}>
                            <div style={{ fontSize: '9.5px', color: C.ink4, letterSpacing: '0.12em' }}>CHARGES</div>
                            <div data-band-figure style={{ fontSize: '16px', fontVariantNumeric: 'tabular-nums', color: C.ink4, fontWeight: 400 }}>{CT.text}</div>
                          </div>
                        </HoverCard>
                      </div>
                    </div>
                  ) : (
                    <NotComputed style={{ marginTop: '6px', maxWidth: '300px' }}>{'P&L UNKNOWN · ' + String(M.reason || 'not computed').toUpperCase()}</NotComputed>
                  )}
                </div>
                <div data-holdings-corner style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', paddingLeft: '14px', borderLeft: '1px solid ' + C.line2, flex: '0 0 auto' }}>
                  <div style={{ fontSize: '9.5px', letterSpacing: '0.12em', color: C.ink4, whiteSpace: 'nowrap' }}>HOLDINGS</div>
                  {(() => { const mO = money(M.computed ? M.holdings : null, 16); return <div data-band-figure style={{ ...mO.style, color: mO.unknown ? C.ink5 : C.ink, fontWeight: M.computed && M.holdings < 0 ? 600 : mO.unknown ? 400 : 400, whiteSpace: 'nowrap' }} data-tip={M.computed ? '' : M.reason || ''}>{mO.text}</div>; })()}
                  <div style={{ fontSize: '9.5px', color: C.ink5, whiteSpace: 'nowrap' }}>outside the day limit</div>
                </div>
              </div>
              <div data-day-limit style={{ display: 'flex', flexDirection: 'column', gap: '5px', cursor: 'default' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <div style={{ fontSize: '9.5px', color: C.ink4, letterSpacing: '0.12em', whiteSpace: 'nowrap', flex: '0 0 auto' }}>DAY LIMIT</div>
                  <div style={{ flex: 1 }} />
                  <div data-limit-head style={{ ...limitHeadStyle, fontSize: '10.5px' }}>{M.computed ? M.head : 'UNKNOWN'}</div>
                </div>
                <div className={M.computed && !M.noLimit ? 'rt-tip' : undefined} data-tip={M.computed && !M.noLimit ? 'AMBER ' + (M.amber != null ? inr0(M.amber) : '—') + ' · RED ' + (M.red != null ? inr0(M.red) : '—') + ' · LIMIT ' + (M.limit != null ? inr0(M.limit) : '—') : undefined} data-tip-head="DAY LIMIT"
                  style={{ position: 'relative', height: '6px', background: '#f1eee8', border: '1px solid ' + C.line, ...(M.computed ? {} : { backgroundImage: 'repeating-linear-gradient(135deg,#efece6 0 5px,#fff 5px 10px)' }) }}>
                  {M.computed && !M.noLimit && <div data-limit-fill style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: Math.min(M.used || 0, 100) + '%', background: M.zone === 'UNDER' || M.up ? C.line4 : M.hue }} />}
                  {M.over && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '100%', background: 'repeating-linear-gradient(135deg,#c1301a 0 3px,#fdeae5 3px 6px)' }} />}
                  {M.amberPct != null && <div style={{ position: 'absolute', top: '-3px', bottom: '-3px', left: M.amberPct + '%', width: '2px', background: C.amber }} />}
                  {M.redPct != null && <div style={{ position: 'absolute', top: '-3px', bottom: '-3px', left: M.redPct + '%', width: '2px', background: C.red }} />}
                </div>
              </div>
            </div>
            <MarginFooter block={P.margin} now={now} />
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <div style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px 6px 14px' }}>
                <div style={{ fontSize: '11px', letterSpacing: '0.14em', color: C.ink, fontWeight: 600 }}>POSITIONS</div>
                {gStep >= 2 && sharedGrade && <div data-shared-grade style={{ ...plainNotGraded, padding: 0 }}>{sharedGrade}</div>}
                <div style={{ flex: 1 }} />
                <div style={{ display: 'flex', gap: '4px' }}>
                  {posSeg.map(x => <button key={x.k} type="button" style={x.style} onClick={() => set({ posView: x.k })}>{x.label}</button>)}
                </div>
              </div>
              {st.posView === 'grouped' ? (
                <div style={{ display: 'flex', alignItems: 'center', fontSize: '9px', color: C.ink4, letterSpacing: '0.1em', padding: '2px 14px 3px 14px' }}>
                  <div style={{ width: '22px', flex: '0 0 22px' }} />
                  <div style={{ flex: 1 }}>STRUCTURE / LEG</div>
                  {vis.intent && <div style={cell('intent', vis)}>INTENT</div>}
                  <div style={{ ...cbox(vis, 'qty'), textAlign: 'right' }}>QTY</div>
                  <div style={{ ...cbox(vis, 'avg'), textAlign: 'right' }}>AVG</div>
                  <div style={{ ...cbox(vis, 'ltp'), textAlign: 'right' }}>LTP</div>
                  <div style={{ ...cbox(vis, 'pl'), textAlign: 'right' }}>P&amp;L</div>
                  {vis.charges && <div style={{ width: CH_W, flex: '0 0 ' + CH_W, textAlign: 'right' }} data-tip="Charges on the round trip: paid, plus ≈ to close while open. Shown only — not in P&L or the day limit.">CHARGES</div>}
                  {vis.stop && <div style={{ ...cbox(vis, 'stop'), paddingLeft: '12px' }}>STOP</div>}
                  <div style={{ width: '60px', flex: '0 0 60px', display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                    <button type="button" className="rt-ib" style={iconBtnSm} data-tip="Expand all groups" onClick={() => set({ open: Object.fromEntries(todayItems.map(x => [x.id, true])) })}><Icon name="unfold_more" /></button>
                    <button type="button" className="rt-ib" style={iconBtnSm} data-tip="Collapse all groups" onClick={() => set({ open: Object.fromEntries(todayItems.map(x => [x.id, false])) })}><Icon name="unfold_less" /></button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', fontSize: '9px', color: C.ink4, letterSpacing: '0.1em', padding: '2px 14px 3px 14px', minHeight: '32px' }}>
                  {flatHeads.filter(hd => vis.intent || hd.key !== 'intent').map(hd => <button key={hd.key} type="button" className="rt-hd" style={hd.style} onClick={hd.pick}>{hd.label}</button>)}
                  {vis.charges && <div style={{ width: CH_W, flex: '0 0 ' + CH_W, textAlign: 'right' }}>CHARGES</div>}
                  {vis.stop && <div style={{ ...cbox(vis, 'stop'), paddingLeft: '12px' }}>STOP</div>}
                  <div style={{ width: '60px', flex: '0 0 60px' }} />
                </div>
              )}
            </div>

            {positionsReason && (
              <NotComputed style={{ margin: '8px 14px' }}>
                {status.feed && status.feed.upstox_signin_needed ? (
                  <>
                    {'POSITION VALUES UNKNOWN · UPSTOX NOT SIGNED IN · '}
                    <a href={UPSTOX_SIGNIN_URL} target="_blank" rel="noopener" onClick={openUpstoxSignin} style={{ color: C.red, fontWeight: 700, letterSpacing: '0.08em' }}
                      data-tip="Sign in to Upstox — you come back to this tab">SIGN IN</a>
                    {' · grades shown as of their time'}
                  </>
                ) : 'POSITION VALUES UNKNOWN · ' + String(positionsReason).toUpperCase() + ' · grades shown as of their time'}
              </NotComputed>
            )}
            <Note text={st.notes.rail} onClose={() => note('rail', null)} fade hold={!!st.notesHold} />
            {!structures.length && (
              <div style={{ padding: '12px 14px 4px 36px', fontSize: '11px', color: C.ink4 }}>No structures today.</div>
            )}

            {st.posView === 'grouped' && structures.map(s => {
              const gl = groupLineParts({ kind: s.kind, gradeLabel: s.gradeLabel, flags: s.flags, note: s.collapsedNote }, gStep, sharedGrade);
              return (
              <div key={s.id} style={s.wrapStyle}>
                <div data-group-row style={{ display: 'flex', alignItems: 'center', minHeight: '38px', boxSizing: 'border-box', padding: '0 14px', cursor: 'pointer' }} onClick={s.toggle} onMouseEnter={s.enter} onMouseLeave={s.leave}>
                  <div style={{ fontSize: '10px', color: C.ink5, width: '22px', flex: '0 0 22px' }}>{s.caret}</div>
                  <div data-groupline data-step={gStep} style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    {renderTitle(s)}
                    {gl.grade && <div style={s.badgeStyle} data-tip={s.badgeTitle}>{gl.grade.text}</div>}
                    <div style={{ ...s.timerStyle, flex: '0 0 auto' }}>{s.timer}</div>
                    {s.hasGauge && <div style={s.gaugeStyle} data-tip={s.gaugeTitle} />}
                    {s.due && <div style={{ fontSize: '10px', color: C.red, fontWeight: 600, whiteSpace: 'nowrap', flex: '0 0 auto' }}>interrupt due</div>}
                    {gl.flags.map(f => <div key={f.key} data-flag={f.key} style={FLAG_BOX} data-tip={f.text !== f.full ? f.full : undefined}>{f.text}</div>)}
                    {gl.note && <div style={{ fontSize: '11px', color: C.ink5, whiteSpace: 'nowrap', flex: '0 0 auto' }}>{gl.note}</div>}
                    {gl.fold.length > 0 && (
                      <div data-fold className="rt-tip" data-tip={gl.fold.map(f => f.text).join('\n')} data-tip-head={'ALSO ON ' + s.name}
                        style={gl.foldTone === 'red'
                          ? { ...FOLD_BOX, color: '#fff', background: C.red, border: '1px solid ' + C.red }
                          : { ...FOLD_BOX, color: C.amber, border: '1px dashed ' + C.amber, textDecoration: 'underline dotted ' + C.amber, textUnderlineOffset: '2px' }}>+{gl.fold.length}</div>
                    )}
                    <div style={s.metaStyle}>{s.meta}</div>
                  </div>
                  <div style={s.plStyle} className={s.plTip ? 'rt-tip' : undefined} data-tip={s.plTip || undefined} data-tip-head="P&L">{s.pl}</div>
                  <ChargesCell c={s.ch} size="12px" open={st.chargesOpen === 'g' + s.id} />
                  {vis.stop && <div style={{ ...cbox(vis, 'stop') }} />}
                  <div style={{ width: '60px', flex: '0 0 60px', display: 'flex', justifyContent: 'flex-end', gap: '2px' }} onClick={e => e.stopPropagation()}>
                    <InertIconBtn icon="logout" title="Exit all legs" exit />
                  </div>
                </div>
                {renderGroupPanels(s)}
                {s.open && (
                  <>
                    {s.legs.map(l => (
                      <div key={l.key}>
                        <LegRow l={l} moving={st.moving === l.key} chOpen={st.chargesOpen === l.key}
                          onMove={() => set(x => ({ moving: x.moving === l.key ? null : l.key, notes: { ...x.notes, moves: null } }))} />
                        {st.moving === l.key && (() => {
                          const against = s.kind === 'confirmed' || s.kind === 'fading';
                          return pickerFrom(moveOptions(s, l),
                            (against ? 'MOVE AND LOG ' : 'MOVE ') + l.sym + ' INTO',
                            against
                              ? 'This structure is against the trend. Moving this leg is logged to weekly review, and both structures are marked REGROUPED for the rest of today. No timer is reset.'
                              : 'Regrouping changes trend grading only. It does not reset a running timer, and it does not move anything out of today’s loss limit — except into a HOLDING group inside the leg’s reclassify window.',
                            against, () => set({ moving: null }), '+ NEW STRUCTURE', seed && seed.picker);
                        })()}
                      </div>
                    ))}
                    <div style={{ height: '9px' }} />
                  </>
                )}
              </div>
              );
            })}

            {st.posView === 'flat' && flatLegs.map(l => (
              <div key={l.key} style={l.rowStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, flexWrap: 'wrap', rowGap: '3px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: C.ink }}>{l.sym}</div>
                  {!vis.stop && l.stopFailed && <span data-no-stop data-tip="This leg's stop failed — it has no stop" style={{ marginLeft: '8px', fontSize: '9px', letterSpacing: '0.1em', fontWeight: 700, padding: '1px 5px', color: C.red, background: C.redTint, border: '1px solid ' + C.red, whiteSpace: 'nowrap' }}>NO STOP</span>}
                  {l.mcx && <span data-mcx style={{ fontSize: '10px', color: C.ink4, whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>{l.mcx}</span>}
                  {l.rejoined && <span style={{ fontSize: '10px', color: C.ink5, marginLeft: '8px', whiteSpace: 'nowrap' }} data-tip="Reopened the same day in the same direction — put back into its group automatically">{l.rejoined}</span>}
                  {l.tags.map((t, i) => <div key={i} style={t.style}>{t.label}</div>)}
                </div>
                <div style={{ width: '110px', flex: '0 0 110px', fontSize: '11px', color: C.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '10px' }} data-tip={l.grName}>{l.grName}</div>
                {vis.intent && <div style={{ ...cbox(vis, 'intent'), fontSize: '9px', letterSpacing: '0.08em', color: C.ink3 }} data-tip={l.intentTitle}>{l.intent}</div>}
                <div style={{ ...cbox(vis, 'qty'), textAlign: 'right', color: C.ink3, fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{l.qty}</div>
                <div style={{ ...cbox(vis, 'avg'), textAlign: 'right', color: C.ink3, fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>{l.avg}</div>
                <div style={{ ...cbox(vis, 'ltp'), textAlign: 'right', color: C.ink2, fontSize: '12px', fontVariantNumeric: 'tabular-nums' }} data-tip={l.ltpTitle}>{l.ltp}{l.ltpUpstox && <span data-ltp-upstox style={{ fontSize: '8px', color: C.amber, marginLeft: '2px', fontWeight: 700 }}>U</span>}</div>
                <div style={l.plStyle} className={l.unknownTitle ? 'rt-tip' : undefined} data-tip={l.unknownTitle || undefined} data-tip-head="P&L">{l.pl}</div>
                <ChargesCell c={l.ch} open={st.chargesOpen === l.key} />
                <StopCell failed={l.stopFailed} />
                <div style={{ width: '60px', flex: '0 0 60px', display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                  <InertIconBtn icon="logout" title="Exit leg" exit />
                </div>
              </div>
            ))}

            <Note text={st.notes.moves} onClose={() => note('moves', null)} fade hold={!!st.notesHold} style={{ margin: '8px 14px 0 14px' }} />

            {orphans.length > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 14px 5px 14px' }}>
                  <div style={{ fontSize: '11px', letterSpacing: '0.14em', color: C.amber, fontWeight: 600 }}>UNCLASSIFIED · IN NO STRUCTURE</div>
                  <div style={{ flex: 1 }} />
                  <div style={{ fontSize: '10px', color: C.ink3, letterSpacing: '0.04em' }}>COUNTS TO DAY LIMIT</div>
                </div>
                {orphans.map(o => {
                  const g = st.grouping && st.grouping.legId === o.key ? st.grouping.mode : null;
                  const pick = mode => set(x => ({ grouping: g === mode ? null : { legId: o.key, mode }, notes: { ...x.notes, orphans: null } }));
                  return (
                    <div key={o.key}>
                      <div style={{ background: C.amberTint, marginBottom: '4px', display: 'flex', alignItems: 'center', padding: '7px 14px' }}>
                        <div style={{ width: '22px', flex: '0 0 22px' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, flexWrap: 'wrap', rowGap: '4px' }} data-tip={o.meta}>
                          <div style={{ fontSize: '14px', color: C.ink, fontWeight: 600 }}>{o.sym}</div>
                          {o.mcx && <span data-mcx style={{ fontSize: '10px', color: C.ink4, whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>{o.mcx}</span>}
                          <div style={badge('ungraded').style}>NOT GRADED · UNCLASSIFIED</div>
                          <div style={{ fontSize: '11px', color: C.amber, fontWeight: 600, whiteSpace: 'nowrap' }}>{o.short}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto', paddingRight: '12px' }}>
                          <button type="button" className="rt-repair" disabled={o.legId == null} style={{ ...btnRepair, ...(g === 'group' ? { background: C.paper2, borderColor: C.amber } : {}), ...(o.legId == null ? disabledLook : {}) }} onClick={() => pick('group')} data-tip={o.legId == null ? NO_LEG_YET : 'Group into a structure — fixes trend grading only, stays in the day limit'}><Icon name="link" /><span>GROUP</span></button>
                          <button type="button" className="rt-repair" disabled={o.legId == null} style={{ ...btnRepair, ...(g === 'holding' ? { background: C.paper2, borderColor: C.amber } : {}), ...(o.legId == null ? disabledLook : {}) }} onClick={() => pick('holding')} data-tip={o.legId == null ? NO_LEG_YET : "Make holding — out of today's loss limit now inside the reclassify window, otherwise from the next session"}><Icon name="inventory_2" /><span>HOLDING</span></button>
                        </div>
                        <div style={o.plStyle} className={o.plTip ? 'rt-tip' : undefined} data-tip={o.plTip || undefined} data-tip-head="P&L">{o.pl}</div>
                        <ChargesCell c={o.ch} size="12px" open={st.chargesOpen === o.key} />
                        {vis.stop && <div style={{ ...cbox(vis, 'stop') }} />}
                        <div style={{ width: '60px', flex: '0 0 60px' }} />
                      </div>
                      {g && pickerFrom(groupOptions(o, g),
                        g === 'holding' ? 'MAKE ' + o.sym + ' A HOLDING IN' : 'GROUP ' + o.sym + ' INTO',
                        g === 'holding'
                          ? 'A holding is outside today’s loss limit — at once while this leg’s reclassify window is open (' + o.short + '), otherwise from the next session. It is never graded.'
                          : 'GROUP puts this leg inside a structure. It fixes the trend grading only — the position stays inside today’s loss limit.',
                        false, () => set({ grouping: null }), g === 'holding' ? '+ NEW HOLDING' : '+ NEW STRUCTURE', seed && seed.picker)}
                    </div>
                  );
                })}
              </>
            )}
            <Note text={st.notes.orphans} onClose={() => note('orphans', null)} fade hold={!!st.notesHold} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 14px 5px 14px' }}>
              <div style={{ fontSize: '11px', letterSpacing: '0.14em', color: C.ink3, fontWeight: 600 }}>HOLDINGS</div>
              <div style={{ flex: 1 }} />
            </div>
            {!holdings.length && <div style={{ padding: '4px 14px 4px 36px', fontSize: '11px', color: C.ink4 }}>No holdings.</div>}
            {holdings.map(h => (
              <div key={h.key} style={{ marginBottom: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px' }}>
                  <div style={{ width: '22px', flex: '0 0 22px' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }} data-tip={h.meta} onMouseEnter={hv('st' + h.key).enter} onMouseLeave={hv('st' + h.key).leave}>
                    {renderTitle({ id: h.key, name: h.name, hovered: st.hov === 'st' + h.key, renameBtnStyle: { width: '24px', height: '24px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', visibility: st.hov === 'st' + h.key ? 'visible' : 'hidden' } })}
                  </div>
                  <div style={h.plStyle} className={h.plTip ? 'rt-tip' : undefined} data-tip={h.plTip || undefined} data-tip-head="P&L">{h.pl}</div>
                  <ChargesCell c={h.ch} size="12px" open={st.chargesOpen === 'h' + h.key} />
                  {vis.stop && <div style={{ ...cbox(vis, 'stop') }} />}
                  <div style={{ width: '60px', flex: '0 0 60px', display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                    <InertIconBtn icon="logout" title="Exit holding" exit />
                  </div>
                </div>
                {renderGroupPanels({ id: h.key, name: h.name })}
                {h.legs.map(l => <LegRow key={l.key} l={l} chOpen={st.chargesOpen === l.key} />)}
                {h.legs.length > 0 && <div style={{ height: '9px' }} />}
              </div>
            ))}

            {/* upstox-053: CLOSED TODAY is a group — totals under P&L and CHARGES; one compact row per position */}
            <div data-closed-header style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '8px 14px', marginTop: '10px', cursor: 'pointer', background: C.closedTint }} onClick={() => set(s => ({ closedOpen: !s.closedOpen }))}>
              <div style={{ fontSize: '10px', color: C.ink5, width: '22px', flex: '0 0 22px' }}>{st.closedOpen ? '▾' : '▸'}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flex: 1, minWidth: 0, whiteSpace: 'nowrap' }}>
                <div style={{ fontSize: '14px', color: C.ink, fontWeight: 600 }}>CLOSED TODAY</div>
                <div style={{ fontSize: '11px', color: C.ink4 }}>{closedLabel}</div>
              </div>
              {[['AVG BUY', closedBuyW(vis)], ['AVG SELL', cbox(vis, 'avg').width], ['LTP', cbox(vis, 'ltp').width]].map(([lbl, w]) => (
                <div key={lbl} style={{ width: w, flex: '0 0 ' + w, textAlign: 'right', fontSize: '9px', color: C.ink4, letterSpacing: '0.1em' }}>{st.closedOpen && closedItems.length ? lbl : ''}</div>
              ))}
              <div data-closed-total-pl style={{ ...closedTotalM.style, ...cbox(vis, 'pl'), textAlign: 'right', fontWeight: 700 }}>{closedItems.length ? closedTotalM.text : ''}</div>
              {vis.charges && <div style={{ width: CH_W, flex: '0 0 ' + CH_W }} onClick={e => e.stopPropagation()}>
                {closedItems.length > 0 && <ChargesCell c={closedTotalCh} size="12px" />}
              </div>}
              <div style={{ width: closedNoteW(vis), flex: '0 0 ' + closedNoteW(vis) }} />
            </div>
            {st.closedOpen && closed.length > 0 && (
              <div data-closed-rows style={{ background: C.closedTint, paddingBottom: '9px' }}>
                {closed.map(c => (
                  <div key={c.key} data-closed-row style={{ display: 'flex', alignItems: 'center', height: LEG_ROW_H, padding: '0 14px', fontSize: '12px' }}>
                    <div style={{ width: '22px', flex: '0 0 22px' }} />
                    {/* upstox-053: name and tag on the left; the grey group · time sits in the
                        stop/actions space at the right, which closed rows do not use — nothing truncates */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flex: 1, minWidth: 0, whiteSpace: 'nowrap', paddingLeft: '4px', paddingRight: '4px' }}>
                      <span style={{ color: C.closedInk, flex: '0 0 auto' }} data-tip={c.name}>{c.name}</span>
                      {c.badge && <span style={{ ...c.badgeStyle, fontSize: '8px', letterSpacing: '0.04em', padding: '1px 4px', flex: '0 0 auto' }} data-tip="in the start copy of an offside episode this session">{c.badge}</span>}
                    </div>
                    <HoverCard text={c.qtyNote} open={st.qtyOpen === c.key} style={{ width: closedBuyW(vis), flex: '0 0 ' + closedBuyW(vis) }}>
                      <div data-avg-buy className="rt-tip" style={{ textAlign: 'right', color: C.closedInk, fontVariantNumeric: 'tabular-nums' }}>{c.avgBuy}</div>
                    </HoverCard>
                    <HoverCard text={c.qtyNote} style={{ ...cbox(vis, 'avg') }}>
                      <div data-avg-sell className="rt-tip" style={{ textAlign: 'right', color: C.closedInk, fontVariantNumeric: 'tabular-nums' }}>{c.avgSell}</div>
                    </HoverCard>
                    <div style={{ ...cbox(vis, 'ltp'), textAlign: 'right', color: C.closedInk, fontVariantNumeric: 'tabular-nums' }}>{c.ltp}</div>
                    <div style={{ ...c.plStyle, color: C.closedInk, fontWeight: 400 }}>{c.pl}</div>
                    <ChargesCell c={c.ch} open={st.chargesOpen === 'c' + c.key} />
                    {vis.stop && <div data-closed-group style={{ width: closedNoteW(vis), flex: '0 0 ' + closedNoteW(vis), paddingLeft: '12px', boxSizing: 'border-box', fontSize: '10px', color: C.ink5, letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      data-tip={String(c.group).toUpperCase() + ' · ' + c.time}>
                      {String(c.group).toUpperCase()} · {c.time}{c.noLeg ? ' · not seen open' : ''}
                    </div>}
                    {!vis.stop && <div style={{ width: '60px', flex: '0 0 60px' }} />}
                  </div>
                ))}
              </div>
            )}
            <div style={{ height: '24px' }} />
          </div>
        </div>
        </ColsCtx.Provider>
        </div>

        {/* ══ TRACK 2 · ORDERS ══ (upstox-050: collapses to a strip; never opens by itself) */}
        {!st.ordersOpen && (
          <PanelStrip name="ORDERS" onOpen={() => set({ ordersOpen: true })}
            counts={[{ text: working.length + ' OPEN' }, awaiting > 0 ? { text: awaiting + ' AWAITING' } : null]}
            title={'Expand orders · ' + working.length + ' open' + (awaiting ? ' · ' + awaiting + ' awaiting intent today' : '')} />
        )}
        {st.ordersOpen && (
        <div data-orders-track style={{ minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, background: '#fff', borderRight: '1px solid ' + C.line }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#fff' }}>
            <div style={{ width: '7px', height: '7px', background: C.ink, flex: '0 0 7px' }} />
            <div style={{ fontSize: '11px', letterSpacing: '0.14em', color: C.ink, fontWeight: 600 }}>OPEN ORDERS</div>
            <div style={{ fontSize: '10px', color: C.ink4, letterSpacing: '0.06em' }}>{working.length}</div>
            <div style={{ flex: 1 }} />
            {awaiting > 0 && (
              <button type="button" onClick={toggleAwaiting} data-tip="Today's orders with no declared intent — show them"
                style={{ background: st.awaitingOpen ? C.paper2 : 'transparent', border: '1px solid ' + (st.awaitingOpen ? C.line3 : 'transparent'), padding: '2px 6px', fontFamily: FONT, fontSize: '10px', color: C.ink3, letterSpacing: '0.06em', fontWeight: 600, cursor: 'pointer' }}>
                {awaiting} AWAITING INTENT {st.awaitingOpen ? '▾' : '▸'}
              </button>
            )}
            <button type="button" className="rt-ib" data-collapse="orders" onClick={() => set({ ordersOpen: false })}
              data-tip="Collapse orders to a strip — it never opens by itself; counts stay visible" style={{ ...iconBtnSm, flex: '0 0 26px' }}><Icon name="keyboard_double_arrow_right" /></button>
          </div>
          <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div data-open-orders style={{ overflow: 'auto', minHeight: 0, flex: '0 1 auto' }}>
              {st.awaitingOpen && (
                <div style={{ background: C.amberTint, marginBottom: '4px' }}>
                  <div style={{ padding: '8px 16px 4px 16px', fontSize: '9px', color: C.amber, letterSpacing: '0.12em', fontWeight: 700 }}>AWAITING INTENT · TODAY · NEWEST FIRST</div>
                  {!st.awaiting && <div style={{ padding: '6px 16px 10px 16px', fontSize: '11px', color: C.ink4 }}>Loading…</div>}
                  {st.awaiting && st.awaiting.error && <Refusal text={st.awaiting.error} style={{ margin: '4px 16px 10px 16px' }} />}
                  {st.awaiting && st.awaiting.items && !st.awaiting.items.length && <div style={{ padding: '6px 16px 10px 16px', fontSize: '11px', color: C.ink4 }}>None — every order has an intent.</div>}
                  {st.awaiting && st.awaiting.items && [...st.awaiting.items, ...(st.awaiting.earlier || [])].map(o => (
                    <OrderLine key={o.upstox_order_id} o={o} sym={o.trading_symbol || symbolOf(o.instrument_key)} showDate={String(o.ts || '').slice(0, 10) !== String(status.session_date || '')}
                      flag={flagOf(o)} open={st.intentOpen === 'a' + o.upstox_order_id}
                      onOpen={() => set({ intentOpen: 'a' + o.upstox_order_id })} onClose={() => set({ intentOpen: null })}
                      declare={declare} onDone={intentDone} initial={seed && seed.intent} />
                  ))}
                  {st.awaiting && st.awaiting.items && !st.awaiting.earlier && (st.awaiting.earlierCount || 0) > 0 && (
                    <div style={{ padding: '8px 16px 10px 16px' }}>
                      <button type="button" onClick={loadEarlier} style={{ ...btnGhost, height: '24px', fontSize: '9px' }}
                        data-tip={'Undeclared orders from ' + ((P.orders && P.orders.intent_tagging_start) || '2026-09-29') + ' up to yesterday — not counted, still taggable'}>
                        SHOW EARLIER ORDERS ({st.awaiting.earlierCount})
                      </button>
                    </div>
                  )}
                  {st.awaiting && st.awaiting.earlierError && <Refusal text={st.awaiting.earlierError} style={{ margin: '4px 16px 10px 16px' }} />}
                </div>
              )}
              {!working.length && <div style={{ padding: '12px 16px', fontSize: '11px', color: C.ink4 }}>No open orders.</div>}
              {working.map(w => (
                <WorkingOrder key={w.key} w={w}>
                  {/* upstox-069 — line 1: name · time ······ TAG / intent · side qty */}
                  <div data-order-line1 style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <FitSymbol full={noYear(w.sym)} style={{ fontSize: '14px', color: C.ink, fontWeight: 500, flex: 1 }} />
                    <div data-order-time style={{ fontSize: '11px', color: C.ink5, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>{String(w.order.ts || '').slice(11, 16)}</div>
                    {w.order.intent
                      ? <div data-order-intent style={{ fontSize: '10px', color: C.ink4, letterSpacing: '0.06em', flex: '0 0 auto' }} data-tip="The declared intent">{String(w.order.intent).toUpperCase()}</div>
                      : <IntentControl slot="button" order={w.order} open={st.intentOpen === 'o' + w.key}
                          onOpen={() => set({ intentOpen: 'o' + w.key })} onClose={() => set({ intentOpen: null })}
                          declare={declare} onDone={intentDone} initial={seed && seed.intent} />}
                    <div data-order-side style={{ fontSize: '12px', color: C.ink, fontWeight: 700, letterSpacing: '0.06em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flex: '0 0 auto' }}>{sideLine(w.order)}</div>
                  </div>
                  {/* line 2 — four fixed, right-aligned columns, each labelled on every order */}
                  <div data-order-line2 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                      {w.on && <><InertIconBtn icon="edit" title="Modify order" /><InertIconBtn icon="close" title="Cancel order" /></>}
                    </div>
                    {[['TYPE', w.terms.type, 56], ['TRIGGER', w.terms.trigger, 82], ['LTP', w.terms.ltp, 78]].map(([lab, val, wd]) => (
                      <div key={lab} data-order-col={lab} style={{ width: wd + 'px', flex: '0 0 ' + wd + 'px', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        <span style={{ fontSize: '8px', color: C.ink5, letterSpacing: '0.08em', marginRight: '4px' }}>{lab}</span>
                        <span style={{ fontSize: '12px', color: lab === 'LTP' && w.cl.amber ? C.amber : C.ink2, fontWeight: lab === 'LTP' && w.cl.amber ? 700 : 400 }}>{val}</span>
                      </div>
                    ))}
                  </div>
                  {w.cl.triggered && <div data-triggered data-tip={'Triggered — by ' + w.cl.by} style={{ fontSize: '10px', color: C.amber, fontWeight: 700, letterSpacing: '0.04em', textAlign: 'right', whiteSpace: 'nowrap' }}>triggered, not filled</div>}
                  <IntentControl slot="panel" order={w.order} open={st.intentOpen === 'o' + w.key}
                    onOpen={() => set({ intentOpen: 'o' + w.key })} onClose={() => set({ intentOpen: null })}
                    declare={declare} onDone={intentDone} initial={seed && seed.intent} />
                  <OrderAmt ch={w.ch} style={{ alignSelf: 'flex-end' }} />
                  {/* the closeness bar replaces the divider: empty at ≥ CLOSE_EMPTY_PCT away, full at the price */}
                  <div data-closeness={w.cl.fill.toFixed(2)} data-closeness-amber={w.cl.amber ? 'true' : 'false'} aria-hidden="true"
                    style={{ position: 'absolute', left: '16px', right: '16px', bottom: 0, height: '3px', background: C.line2 }}>
                    <div style={{ width: (w.cl.fill * 100).toFixed(1) + '%', height: '100%', background: w.cl.amber || w.cl.triggered ? C.amber : C.ink4 }} />
                  </div>
                </WorkingOrder>
              ))}
            </div>

            <div data-completed style={{ flex: '1 1 0', minHeight: '44px', display: 'flex', flexDirection: 'column', background: '#fff' }}>
              <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '7px', padding: '11px 12px', cursor: 'pointer', background: C.paper3 }} onClick={() => set(s => ({ doneOpen: !s.doneOpen }))}>
                <div style={{ fontSize: '10px', color: C.ink5, width: '10px' }}>{st.doneOpen ? '▾' : '▸'}</div>
                <div style={{ fontSize: '11px', letterSpacing: '0.12em', color: C.ink2, fontWeight: 600, whiteSpace: 'nowrap', flex: '0 0 auto' }}>COMPLETED TODAY</div>
                <div style={{ fontSize: '11px', color: C.ink3, whiteSpace: 'nowrap', flex: '0 0 auto' }}>{nFilled + ' filled · ' + nCancelled + ' cancelled' + (rejects.length ? ' ·' : '')}</div>
                {rejects.length > 0 && <div style={{ fontSize: '10px', color: C.red, background: C.redTint, border: '1px solid ' + C.redLine, padding: '2px 7px', letterSpacing: '0.06em', fontWeight: 700, whiteSpace: 'nowrap', flex: '0 0 auto' }}>{rejects.length} REJECTED</div>}
                <div style={{ flex: 1, minWidth: '6px' }} />
                <div style={{ fontSize: '11px', color: C.ink4, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }} data-tip={'Charges today · ' + CT.title}>{CT.text}</div>
              </div>
              {st.doneOpen && (
                <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', background: '#fff' }}>
                  {done.map(o => (
                    <OrderLine key={o.upstox_order_id} o={o} sym={o.trading_symbol || symbolOf(o.instrument_key)}
                      flag={flagOf(o)} open={st.intentOpen === 'o' + o.upstox_order_id} chOpen={st.chargesOpen === 'o' + o.upstox_order_id} tipOpen={st.edgeTip === o.upstox_order_id}
                      onOpen={() => set({ intentOpen: 'o' + o.upstox_order_id })} onClose={() => set({ intentOpen: null })}
                      declare={declare} onDone={intentDone} initial={seed && seed.intent} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}
        {/* ══ TRACK 3 · WATCHLIST (upstox-043; upstox-050: collapses to a strip) ══ */}
        {st.watchOpen
          ? <Watchlist watch={watch} error={watchError} api={api} onChanged={refreshWatch} initial={seed && seed.watch} held={heldIndex(P)} onCollapse={() => set({ watchOpen: false })} />
          : <PanelStrip name="WATCHLIST" onOpen={() => set({ watchOpen: true })} />}
      </div>

      {/* INTERRUPT */}
      {ov && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(58,52,44,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ width: '880px', maxWidth: 'calc(100% - 32px)', background: '#fff', border: '2px solid ' + C.red }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 22px', borderBottom: '1px solid ' + C.redLine, background: C.redTint }}>
              <div style={{ width: '9px', height: '9px', background: C.red }} />
              <div style={{ fontSize: '13px', letterSpacing: '0.14em', color: C.red, fontWeight: 600 }}>HELD AGAINST A CONFIRMED TREND</div>
              <div style={{ flex: 1 }} />
              <div style={{ fontSize: '14px', color: C.ink, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{ov.timer}</div>
            </div>
            <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div style={{ fontSize: '19px', color: C.ink, lineHeight: 1.45, maxWidth: '620px' }}>{ov.head}</div>
              <div style={{ display: 'flex', gap: '28px', borderTop: '1px solid ' + C.line, borderBottom: '1px solid ' + C.line, padding: '14px 0' }}>
                {ov.facts.map(x => (
                  <div key={x.k} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ fontSize: '10px', color: C.ink4, letterSpacing: '0.08em' }}>{x.k}</div>
                    <div style={{ fontSize: '18px', color: x.c, fontVariantNumeric: 'tabular-nums', fontWeight: x.w }}>{x.v}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {ov.choices.map(c => (
                  <button key={c.kind} type="button" disabled={busy('int')} onClick={() => answer(c.response)}
                    data-tip={c.kind === 'hold' ? 'Write the reason next' : 'Records your decision once — it cannot be changed'}
                    style={{ flex: 1, padding: '16px', textAlign: 'left', cursor: busy('int') ? 'wait' : 'pointer', fontFamily: FONT, border: c.kind === 'exit' ? 'none' : '1px solid ' + (c.kind === 'hold' ? C.red : C.line4), background: c.kind === 'exit' ? C.red : '#fff', color: c.kind === 'exit' ? '#fff' : C.ink, ...(c.kind === 'hold' && st.holdChosen ? { background: C.redTint } : {}) }}>
                    <div style={{ fontSize: '14px', letterSpacing: '0.1em', fontWeight: 600 }}>{c.label}</div>
                    <div style={{ fontSize: '11px', opacity: 0.75, marginTop: '5px', textAlign: 'left' }}>{c.sub}</div>
                  </button>
                ))}
              </div>
              {st.holdChosen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: C.redTint, border: '1px solid ' + C.redLine, padding: '12px 14px' }}>
                  <div style={{ fontSize: '10px', color: C.ink, letterSpacing: '0.08em', fontWeight: 600 }}>WHAT MAKES HOLDING RIGHT? · REQUIRED · GOES TO WEEKLY REVIEW</div>
                  <textarea value={st.intWhy} onChange={e => set({ intWhy: e.target.value })} rows={3} style={textareaStyle} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <button type="button" onClick={commitHold} disabled={!intWhyOk || busy('int')}
                      style={{ height: '44px', padding: '0 20px', background: intWhyOk ? C.ink : C.paper3, color: intWhyOk ? '#fff' : C.ink5, border: '1px solid ' + (intWhyOk ? C.ink : C.line3), fontFamily: FONT, fontSize: '12px', letterSpacing: '0.1em', fontWeight: 600, cursor: intWhyOk ? 'pointer' : 'not-allowed' }}>HOLD AND LOG</button>
                    <span style={{ fontSize: '10px', color: C.ink3 }}>{st.intWhy.trim().length + ' characters · minimum ' + minWhy}</span>
                    <span style={{ flex: 1 }} />
                    <button type="button" onClick={() => set({ holdChosen: false })}
                      style={{ height: '44px', padding: '0 14px', background: '#fff', color: C.ink3, border: '1px solid ' + C.line4, fontFamily: FONT, fontSize: '11px', letterSpacing: '0.08em', cursor: 'pointer' }}>BACK TO CHOICES</button>
                  </div>
                </div>
              )}
              <Refusal text={errOf('int')} style={{ marginTop: 0 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontSize: '10px', color: C.ink4, letterSpacing: '0.04em', flex: 1 }}>ONE INTERRUPT PER EPISODE. YOU WILL NOT BE ASKED AGAIN ABOUT THIS STRUCTURE UNLESS IT CLEARS AND RE-OFFENDS.</div>
                <button type="button" style={{ ...btnGhost, height: '32px' }} data-tip="Hide until you open it again from the rail — it stays unanswered"
                  onClick={() => set(s => ({ interruptFor: null, hiddenEpisodes: { ...s.hiddenEpisodes, [ov.episodeId]: true } }))}>NOT NOW · VIEW SCREEN</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </OrdersNoteCtx.Provider>
  );
}

function StopCell({ failed }) {
  const vis = useCols();
  if (!vis.stop) return null;
  if (failed) return <div style={{ ...cbox(vis, 'stop'), paddingLeft: '12px', display: 'flex', alignItems: 'center' }}><span data-no-stop style={{ fontSize: '9px', letterSpacing: '0.1em', fontWeight: 700, padding: '1px 5px', color: C.red, background: C.redTint, border: '1px solid ' + C.red }}>NO STOP</span></div>;
  return (
    <div style={{ ...cbox(vis, 'stop'), paddingLeft: '12px', display: 'flex', alignItems: 'center', minHeight: '18px' }} data-tip={STOP_TITLE}>
      <div style={{ width: '9px', height: '1px', flex: '0 0 9px', background: C.line4, cursor: 'default' }} />
    </div>
  );
}

// upstox-045: leg rows inside a group are 25 px (never below 24, for the move /
// exit icons) so a group's legs read as one block and groups stay apart
const LEG_ROW_H = '25px';
const legIconBtn = { width: '24px', height: '24px', flex: '0 0 24px' };

// upstox-046: the CHARGES column — narrow, right of P&L, secondary tone; the
// round trip (paid + ≈ to close), its split on hover; unknown is "+ ?", never 0
const CH_W = '84px';
// the hover: the design's dark popover (popStyle), one line per part
// upstox-074: the shared hover card (hoverCard.jsx) — this keeps the call sites: a box carrying a tip
function HoverCard({ text, head, tone, open, dotted, children, style }) {
  return <div className={dotted ? 'rt-tip' : undefined} data-tip={text || undefined} data-tip-head={head || undefined}
    data-tip-tone={tone || undefined} data-tip-open={open ? 'true' : undefined} style={{ position: 'relative', ...style }}>{children}</div>;
}

function ChargesCell({ c, size = '11px', open }) {
  if (!useCols().charges) return null;
  const v = c || { text: '', title: '' };
  return (
    <HoverCard text={v.title} head={v.head} open={open} style={{ width: CH_W, flex: '0 0 ' + CH_W }}>
      <div data-charges className={v.title && v.text !== '—' ? 'rt-tip' : undefined} style={{ textAlign: 'right', fontSize: size, color: v.unknown ? C.ink5 : C.ink4, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', cursor: 'default' }}>{v.text}</div>
    </HoverCard>
  );
}

// upstox-053: names without the year — "SENSEX 69500 PE 08 OCT 26" → "SENSEX 69500 PE 08 OCT"
export function noYear(sym) {
  const m = String(sym || '').match(/^(.* \d{2} [A-Z]{3}) \d{2}$/);
  return m ? m[1] : (sym || '');
}

// upstox-053: an order's exception tag — none for a plain fill
export function exceptionOf(o) {
  const st = String(o.order_status || '').toLowerCase();
  const grp = o.status_group || (st === 'rejected' ? 'REJECTED' : st === 'cancelled' ? 'CANCELLED' : st === 'complete' ? 'FILLED' : 'OPEN');
  const filled = o.filled_quantity || 0;
  if (grp === 'REJECTED') return { text: 'REJECTED', kind: 'red', title: o.status_message || 'no reason given' };
  if ((grp === 'CANCELLED' || grp === 'PARTIAL') && filled > 0) return { text: 'PARTIAL ' + filled + '/' + o.quantity, kind: 'amber', title: grp === 'PARTIAL' ? 'part filled, still working' : 'part filled, then cancelled' };
  if (grp === 'CANCELLED') return { text: 'CANCELLED', kind: 'amber', title: o.status_message || '' };
  if (grp === 'OPEN') return { text: 'OPEN', kind: 'grey', title: 'still working' };
  return null;
}

/**
 * upstox-055: an order's exception as a thin left-edge bar, not a tag column.
 * red — REJECTED, AFTER LIMIT (and AFTER RED, the same family); amber —
 * CANCELLED; PARTIAL — no bar, the quantity reads "filled/ordered". The
 * labels and Upstox's message show on hover and keyboard focus, and are in
 * the row's text for screen readers (the colour is never the only signal).
 */
export function edgeOf(o, flag) {
  const st = String(o.order_status || '').toLowerCase();
  const grp = o.status_group || (st === 'rejected' ? 'REJECTED' : st === 'cancelled' ? 'CANCELLED' : st === 'complete' ? 'FILLED' : 'OPEN');
  const filled = o.filled_quantity || 0;
  const labels = [];
  let color = null;
  if (grp === 'REJECTED') { labels.push('REJECTED'); color = 'red'; }
  if (flag) { labels.push(flag); color = 'red'; }
  if (grp === 'CANCELLED') { labels.push('CANCELLED'); color = color || 'amber'; }
  const partial = filled > 0 && filled < (o.quantity || 0) && (grp === 'PARTIAL' || grp === 'CANCELLED');
  if (partial) labels.push('PARTIAL ' + filled + '/' + o.quantity);
  if (!labels.length) return null;
  const msg = o.status_message || (grp === 'REJECTED' ? 'no reason given' : '');
  return { color, labels, partial, message: msg, text: labels.join(' · ') + (msg ? ' — ' + msg : '') };
}

/** "NIFTY 25000 CE 06 OCT" → "NIFTY 25000 CE" (the expiry is the first thing cut). */
export function noExpiry(sym) {
  const m = String(sym || '').match(/^(.*) \d{2} [A-Z]{3}( \d{2})?$/);
  return m ? m[1] : (sym || '');
}

/** upstox-055: the symbol in full; too narrow → without the expiry; still too
 * narrow → truncated with "…". The full name is always on hover. */
function FitSymbol({ full, style }) {
  const ref = useRef(null);
  const [short, setShort] = useState(false);
  const shortText = noExpiry(full);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => {
      if (!ref.current) return;
      setShort(false);
      requestAnimationFrame(() => { if (ref.current && ref.current.scrollWidth > ref.current.clientWidth + 1) setShort(true); });
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [full]);
  return <div ref={ref} data-symbol={short ? 'short' : 'full'} data-tip={full} style={{ ...style, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{short && shortText !== full ? shortText : full}</div>;
}

const EDGE = { red: C.red, amber: C.amber };
const srOnly = { position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

/** The edge bar's tooltip: hover or keyboard focus on the row shows the labels and the message. */
function EdgeTip({ edge, show }) {
  if (!edge || !show) return null;
  return (
    <div data-edge-tip role="tooltip" style={{ ...popStyle, top: 'calc(100% - 2px)', left: '12px', whiteSpace: 'normal', maxWidth: '330px', lineHeight: 1.5 }}>
      <div style={{ fontWeight: 600, letterSpacing: '0.06em' }}>{edge.labels.join(' · ')}</div>
      {edge.message && <div style={{ color: '#d9d4ca' }}>{edge.message}</div>}
    </div>
  );
}

/** Row props for an edge bar: inset 3 px stripe (no layout shift), focusable, hover/focus tip. */
function useEdge(edge, forced) {
  const [on, setOn] = useState(false);
  const props = edge ? {
    tabIndex: 0, 'data-edge': edge.color || 'none', 'aria-label': edge.text,
    onMouseEnter: () => setOn(true), onMouseLeave: () => setOn(false),
    onFocus: () => setOn(true), onBlur: () => setOn(false),
  } : {};
  const style = edge && edge.color ? { boxShadow: 'inset 3px 0 0 ' + EDGE[edge.color] } : {};
  return { props, style, show: on || !!forced };
}

/**
 * upstox-053: one order on one line — `13:07  SENSEX 73000 CE 08 OCT  SELL 1000 @ 164.97  ≈₹329  [TAG]`.
 * upstox-055: exceptions as a left-edge bar (edgeOf); PARTIAL in the quantity.
 * Untagged → a small amber TAG opening the intent control below; tagged → the intent in grey.
 */
function OrderLine({ o, sym, flag, open, onOpen, onClose, declare, onDone, initial, chOpen, showDate, tipOpen }) {
  const edge = edgeOf(o, flag);
  const eg = useEdge(edge, tipOpen);
  const ts = String(o.ts || '');
  const when = showDate ? ts.slice(5, 10).split('-').reverse().join('/') + ' ' + ts.slice(11, 16) : ts.slice(11, 16);
  const rejected = String(o.status_group || o.order_status || '').toUpperCase() === 'REJECTED';
  const fl = fillLine(o);                         // upstox-083: a completed order reads its average fill price
  return (
    <div data-order-line {...eg.props} style={{ position: 'relative', background: rejected ? C.redTint : 'transparent', outline: 'none', ...eg.style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '28px', padding: '0 12px' }}>
        <div style={{ fontSize: '10.5px', color: C.ink3, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto', width: showDate ? '70px' : '32px' }}>{when}</div>
        <FitSymbol full={noYear(sym)} style={{ fontSize: '11.5px', color: C.ink, flex: 1 }} />
        <div data-order-side className={fl.reason ? 'rt-tip' : undefined} data-tip={fl.reason || undefined} style={{ fontSize: '12px', color: C.ink, fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>{fl.text}</div>
        {fl.limit && <div data-order-limit style={{ fontSize: '10px', color: C.ink5, whiteSpace: 'nowrap', flex: '0 0 auto' }}>{fl.limit}</div>}
        {o.tag && <div data-kite-tag data-tip="Kite's own tag on this order" style={{ fontSize: '10px', color: C.ink5, whiteSpace: 'nowrap', flex: '0 0 auto' }}>{o.tag}</div>}
        {edge && <span style={srOnly}>{edge.text}</span>}
        <OrderAmt ch={orderCharges(o.charges)} open={chOpen} style={{ width: '46px', flex: '0 0 46px' }} />
        {o.intent
          ? <div style={{ width: '34px', flex: '0 0 34px', textAlign: 'right', fontSize: '8.5px', letterSpacing: '0.06em', color: C.ink4, fontWeight: 600 }} data-tip="the declared intent">{o.intent}</div>
          : <button type="button" data-tag-btn onClick={open ? onClose : onOpen} data-tip="Declare this order's intent — once, permanently"
              style={{ width: '34px', flex: '0 0 34px', height: '20px', padding: 0, background: open ? C.amberTint : '#fff', color: C.amber, border: '1px solid ' + C.amberLine, fontFamily: FONT, fontSize: '9px', letterSpacing: '0.08em', fontWeight: 700, cursor: 'pointer' }}>TAG</button>}
      </div>
      <EdgeTip edge={edge} show={eg.show && !open} />
      {open && !o.intent && (
        <div style={{ padding: '0 16px 8px 16px' }}>
          <IntentControl order={o} open onOpen={onOpen} onClose={onClose} declare={declare} onDone={onDone} initial={initial} />
        </div>
      )}
    </div>
  );
}

/** upstox-055: one open (working) order — the same edge bar and tooltip. */
function WorkingOrder({ w, children }) {
  const edge = edgeOf(w.order, w.flag);
  const eg = useEdge(edge, false);
  return (
    <div data-working-order {...eg.props} style={{ position: 'relative', padding: '12px 16px 14px 16px', display: 'flex', flexDirection: 'column', gap: '6px', outline: 'none', ...eg.style }}
      onMouseEnter={() => { w.enter(); eg.props.onMouseEnter && eg.props.onMouseEnter(); }}
      onMouseLeave={() => { w.leave(); eg.props.onMouseLeave && eg.props.onMouseLeave(); }}>
      {edge && <span style={srOnly}>{edge.text}</span>}
      {children}
      <EdgeTip edge={edge} show={eg.show} />
    </div>
  );
}

function OrderAmt({ ch, style, open }) {
  if (!ch) return null;
  return (
    <HoverCard text={ch.title} open={open} style={style}>
      <div data-order-charges className="rt-tip" style={{ fontSize: '10px', color: ch.unknown ? C.ink5 : C.ink4, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', textAlign: 'right' }}>{ch.text}</div>
    </HoverCard>
  );
}

function LegRow({ l, onMove, moving, chOpen }) {
  const vis = useCols();
  return (
    <div data-leg-row style={{ display: 'flex', alignItems: 'center', height: LEG_ROW_H, padding: '0 14px', fontSize: '12px' }}>
      <div style={{ width: '22px', flex: '0 0 22px' }} />
      <div style={{ flex: 1, minWidth: 0, color: C.ink2, paddingLeft: '4px' }}>{l.sym}{!vis.stop && l.stopFailed && <span data-no-stop data-tip="This leg's stop failed — it has no stop" style={{ marginLeft: '8px', fontSize: '9px', letterSpacing: '0.1em', fontWeight: 700, padding: '1px 5px', color: C.red, background: C.redTint, border: '1px solid ' + C.red, whiteSpace: 'nowrap' }}>NO STOP</span>}{l.mcx && <span data-mcx style={{ marginLeft: '8px', fontSize: '10px', color: C.ink4, whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>{l.mcx}</span>}{l.rejoined && <span style={{ fontSize: '10px', color: C.ink5, marginLeft: '8px', whiteSpace: 'nowrap' }} data-tip="Reopened the same day in the same direction — put back into its group automatically">{l.rejoined}</span>}</div>
      {vis.intent && <div style={{ ...cbox(vis, 'intent'), fontSize: '9px', letterSpacing: '0.08em', color: C.ink3 }} data-tip={l.intentTitle}>{l.intent}</div>}
      <div style={{ ...cbox(vis, 'qty'), textAlign: 'right', color: C.ink3, fontVariantNumeric: 'tabular-nums' }}>{l.qty}</div>
      <div style={{ ...cbox(vis, 'avg'), textAlign: 'right', color: C.ink3, fontVariantNumeric: 'tabular-nums' }}>{l.avg}</div>
      <div style={{ ...cbox(vis, 'ltp'), textAlign: 'right', color: C.ink2, fontVariantNumeric: 'tabular-nums' }} data-tip={l.ltpTitle}>{l.ltp}{l.ltpUpstox && <span data-ltp-upstox style={{ fontSize: '8px', color: C.amber, marginLeft: '2px', fontWeight: 700 }}>U</span>}</div>
      <div style={l.plStyle} className={l.unknownTitle ? 'rt-tip' : undefined} data-tip={l.unknownTitle || undefined} data-tip-head="P&L">{l.pl}</div>
      <ChargesCell c={l.ch} open={chOpen} />
      <StopCell failed={l.stopFailed} />
      <div style={{ width: '60px', flex: '0 0 60px', display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
        {onMove && <IconBtn icon="drive_file_move" title="Move this leg to another structure" onClick={onMove}
          style={{ ...legIconBtn, ...(moving ? { background: C.line2, border: '1px solid ' + C.line4 } : {}) }} />}
        <InertIconBtn icon="logout" title="Exit leg" style={legIconBtn} exit />
      </div>
    </div>
  );
}
